import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DateInputField from '../../components/DateInputField';
import CollapsibleModuleHeaderCard from '../../components/CollapsibleModuleHeaderCard';
import {
  buildSrhtCourseDayCalendarMonths,
  isIsoDateWithinRange,
  SRHT_COURSE_WEEKDAY_LABELS,
  toggleIsoDateSelection,
} from './srhtCourseCalendar';
import {
  closeSrhtCourse,
  createSrhtCourse,
  deleteSrhtCourse,
  exportSrhtCoursePdf,
  exportSrhtParticipantExclusionPdf,
  finalizeSrhtCandidateFeedback,
  finalizeSrhtInstructorFeedback,
  finalizeSrhtParticipantExclusion,
  finalizeSrhtWeeklyEvaluation,
  getSrhtCourse,
  getSrhtCourseReferenceOptions,
  getSrhtCourses,
  getSrhtWeekOverview,
  saveSrhtParticipantExclusion,
  saveSrhtCandidateFeedback,
  saveSrhtInstructorFeedback,
  saveSrhtWeeklyEvaluation,
  unlockSrhtCourse,
  unlockSrhtCandidateFeedback,
  unlockSrhtInstructorFeedback,
  unlockSrhtParticipantExclusion,
  unlockSrhtWeeklyEvaluation,
  updateSrhtCourse,
} from '../../api/srhtCourses';
import {
  deleteFeedbackAsset,
  getFeedbackOptions,
  uploadFeedbackAssets,
} from '../../api/feedback';
import {
  buildEmptySrhtExclusionReport,
  buildInitialSrhtExclusionForm,
  SRHT_EXCLUSION_MAX_REPORTS,
  validateSrhtExclusionForm,
} from './srhtCourseExclusionUtils';
import {
  buildIncidentEditFormState,
  buildIncidentSubmitPayload,
  getInitialIncidentFormState,
  IncidentFeedbackFormFields,
  isIncidentFormValid,
} from '../rueckmeldung/incidentFormShared';
import {
  getVisibleSrhtTabs,
  SRHT_TAB_CANDIDATE,
  SRHT_TAB_EVALUATION,
  SRHT_TAB_INSTRUCTOR,
  SRHT_TAB_MANAGE,
} from './srhtCourseTabs';
import { getSrhtCategoryScalePercent, getSrhtCategoryScaleTone } from './srhtCourseScoreScale';
import { downloadPdfResponse, extractPdfErrorMessage } from '../../utils/pdfDownload';
import { readPersistedTab, writePersistedTab } from '../../utils/tabPersistence';

const DECISION_OPTIONS = [
  {
    value: 'continue_clear',
    label: 'Der Lehrgang kann ohne Bedenken mit dem Teilnehmer fortgesetzt werden',
  },
  {
    value: 'continue_with_concerns',
    label: 'Der Lehrgang kann mit dem Teilnehmer fortgesetzt werden. Der Teilnehmer wird darüber informiert, dass es seitens der Lehrgangsleitung Zweifel an der Eignung gibt.',
  },
  {
    value: 'stop_for_safety',
    label: 'Die Lehrgangsleitung sieht erhebliche Sicherheitsrisiken für den Teilnehmer selbst und den Rest des Lehrgangs, wenn dieser weiterhin teilnimmt.',
  },
];

const SCORE_OPTIONS = [
  { value: 0, label: 'Negativ (0)' },
  { value: 1, label: 'Neutral (1)' },
  { value: 2, label: 'Positiv (2)' },
];

const SRHT_COURSE_HEADER_COLLAPSE_STORAGE_KEY = 'srht-course.header.expanded.v1';
const SRHT_COURSE_ACTIVE_TAB_STORAGE_KEY = 'srht-course.active-tab.v1';
const SRHT_COURSE_ALLOWED_TABS = [
  SRHT_TAB_MANAGE,
  SRHT_TAB_CANDIDATE,
  SRHT_TAB_INSTRUCTOR,
  SRHT_TAB_EVALUATION,
];

const INSTRUCTOR_REVIEW_FIELDS = [
  { key: 'knowledgeScore', label: 'Fachwissen' },
  { key: 'singleMeasuresScore', label: 'Einzelmaßnahme' },
  { key: 'independenceScore', label: 'Selbstständigkeit' },
  { key: 'errorManagementScore', label: 'Fehlermanagement' },
  { key: 'teamworkScore', label: 'Teamfähigkeit' },
];

const SRHT_INSTRUCTOR_REVIEW_DAY_LIMIT = 13;
const SRHT_INSTRUCTOR_DAY_TILE_COUNT = 15;

const COURSE_PDF_OPTIONS = [
  {
    value: 'course-report-all',
    label: 'Gesamtbericht Lehrgang',
    section: 'combined',
    requiresParticipantSelection: false,
    singlePdfPerParticipant: false,
  },
  {
    value: 'course-report-selection',
    label: 'Sammelbericht Teilnehmer',
    section: 'combined',
    requiresParticipantSelection: true,
    singlePdfPerParticipant: false,
  },
  {
    value: 'course-report-single',
    label: 'Einzelbericht Teilnehmer',
    section: 'combined',
    requiresParticipantSelection: true,
    singlePdfPerParticipant: true,
  },
  {
    value: 'candidate-feedback',
    label: 'Tagesfeedback Teilnehmer',
    section: 'candidate-feedback',
    requiresParticipantSelection: false,
    singlePdfPerParticipant: false,
  },
  {
    value: 'instructor-feedback',
    label: 'Tagesfeedback Lehrhöhenretter',
    section: 'instructor-feedback',
    requiresParticipantSelection: false,
    singlePdfPerParticipant: false,
  },
  {
    value: 'weekly-evaluations',
    label: 'Wochenauswertung',
    section: 'weekly-evaluations',
    requiresParticipantSelection: false,
    singlePdfPerParticipant: false,
  },
];

const SRHT_SAFETY_ANSWER_YES = 'yes';
const SRHT_SAFETY_ANSWER_NO = 'no';
const SRHT_ABSENCE_DECISION_WITHDRAW = 'withdraw';
const SRHT_ABSENCE_DECISION_CONTINUE_WITHOUT_EXAM = 'continue_without_exam';

const SRHT_ABSENCE_DECISION_OPTIONS = [
  {
    value: SRHT_ABSENCE_DECISION_WITHDRAW,
    label: 'Teilnehmer scheidet direkt aus dem Lehrgang aus',
  },
  {
    value: SRHT_ABSENCE_DECISION_CONTINUE_WITHOUT_EXAM,
    label: 'Teilnehmer nimmt weiterhin am Lehrgang teil, tritt jedoch nicht zur Prüfung an',
  },
];

function formatDate(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '-';
  }

  const dateValue = new Date(rawValue);
  if (Number.isNaN(dateValue.getTime())) {
    return rawValue;
  }

  return dateValue.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatAverage(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  return Number(numericValue.toFixed(2)).toString().replace('.', ',');
}

function getStatusChipColor(status) {
  if (status === 'finalized') return 'success';
  if (status === 'draft') return 'warning';
  return 'default';
}

function getSrhtFeedbackStatusLabel(status) {
  return status === 'finalized' ? 'Finalisiert' : 'Offen';
}

function getParticipantOverallSummary(participant) {
  return (participant?.weeklySummaries || []).find((item) => Number(item.weekNumber) === 3) || null;
}

function isParticipantExamBlocked(participant) {
  return participant?.exam_admission_status === 'blocked_absence'
    || participant?.examAdmissionStatus === 'blocked_absence';
}

function getParticipantExamBlockLabel(participant) {
  return participant?.examAdmissionStatusLabel || 'Zu viel Fehlzeiten';
}

function getParticipantWithdrawalLabel(participant) {
  return participant?.exclusionRecord?.withdrawnOnDayLabel
    || participant?.statusLabel
    || (participant?.withdrawn_on_day_id ? `Ausgeschieden ab Tag ${participant.withdrawn_on_day_id}` : 'Ausgeschieden');
}

function buildInitialInstructorIncidentForm(courseDate, safetyIncident = null) {
  if (safetyIncident) {
    return buildIncidentEditFormState({
      event_date: safetyIncident.eventDate || courseDate || '',
      title: safetyIncident.title || '',
      description: safetyIncident.description || '',
      payload: safetyIncident.payload || {},
    });
  }

  return {
    ...getInitialIncidentFormState(),
    eventDate: courseDate || '',
  };
}

function getInstructorIncidentValidationMessage({ requireFinalize, safetyIncidentAnswer, incidentFormState }) {
  if (requireFinalize && ![SRHT_SAFETY_ANSWER_YES, SRHT_SAFETY_ANSWER_NO].includes(String(safetyIncidentAnswer || '').trim())) {
    return 'Bitte die Frage nach sicherheitsrelevanten Zwischenfällen mit Ja oder Nein beantworten.';
  }

  if (String(safetyIncidentAnswer || '').trim() === SRHT_SAFETY_ANSWER_YES && !isIncidentFormValid(incidentFormState)) {
    return 'Bitte die Eingabemaske für den sicherheitsrelevanten Zwischenfall vollständig ausfüllen.';
  }

  return '';
}

function getCourseStatusChipColor(status) {
  if (status === 'closed') return 'success';
  if (status === 'draft') return 'warning';
  if (status === 'active') return 'primary';
  return 'default';
}

function getCourseStatusLabel(status) {
  if (status === 'closed') return 'Abgeschlossen';
  if (status === 'draft') return 'Entwurf';
  if (status === 'active') return 'Aktiv';
  if (status === 'archived') return 'Archiviert';
  return String(status || 'Entwurf');
}

function buildCourseAdminForm(course) {
  return {
    label: course?.label || '',
    startDate: course?.start_date || '',
    endDate: course?.end_date || '',
    notes: course?.notes || '',
    totalHours: course?.total_hours ?? 120,
  };
}

const MULTILINE_PLACEHOLDER_SX = {
  '& .MuiInputBase-inputMultiline::placeholder': {
    color: 'rgba(0, 0, 0, 0.6)',
    whiteSpace: 'normal',
    opacity: 1,
  },
};

function normalizeMultiSelectValue(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' && value) {
    return value.split(',');
  }
  return [];
}

function hasSelectedReviewScore(value) {
  if (value === '' || value === null || typeof value === 'undefined') {
    return false;
  }
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue >= 0 && numericValue <= 2;
}

function doesInstructorDayRequireParticipantReviews(selectedDayId, courseDayIndexById) {
  const selectedDayIndex = courseDayIndexById.get(String(selectedDayId || '')) || 0;
  return selectedDayIndex > 0 && selectedDayIndex <= SRHT_INSTRUCTOR_REVIEW_DAY_LIMIT;
}

function getRelevantInstructorParticipants(participants, selectedDayId, courseDayIndexById) {
  if (!doesInstructorDayRequireParticipantReviews(selectedDayId, courseDayIndexById)) {
    return [];
  }

  const selectedDayIndex = courseDayIndexById.get(String(selectedDayId || '')) || 0;
  return (participants || []).filter((participant) => {
    const withdrawnDayIndex = participant?.withdrawn_on_day_id
      ? courseDayIndexById.get(String(participant.withdrawn_on_day_id)) || null
      : null;

    return !withdrawnDayIndex || selectedDayIndex < withdrawnDayIndex;
  });
}

function buildInstructorWorkstepValidation({ instructorUserIds, dayReviewDrafts, participants }) {
  const missingScoreFieldsByParticipantId = {};
  const missingParticipantEntries = [];

  (participants || []).forEach((participant) => {
    const draft = dayReviewDrafts?.[participant.id] || {};
    const missingFields = INSTRUCTOR_REVIEW_FIELDS.filter((field) => !hasSelectedReviewScore(draft[field.key]));
    if (missingFields.length > 0) {
      missingScoreFieldsByParticipantId[participant.id] = missingFields.map((field) => field.key);
      missingParticipantEntries.push({
        participantId: participant.id,
        fullName: participant.full_name || '-',
        missingLabels: missingFields.map((field) => field.label),
      });
    }
  });

  return {
    missingInstructorSelection: !Array.isArray(instructorUserIds) || instructorUserIds.length === 0,
    missingScoreFieldsByParticipantId,
    missingParticipantEntries,
    hasErrors: (!Array.isArray(instructorUserIds) || instructorUserIds.length === 0) || missingParticipantEntries.length > 0,
  };
}

function buildWeeklyEvaluationValidation(form = {}) {
  return {
    missingDecisionCode: !String(form?.decisionCode || '').trim(),
    missingFeedbackText: !String(form?.feedbackText || '').trim(),
    missingFeedbackCourseDayId: !String(form?.feedbackCourseDayId || '').trim(),
    missingFeedbackGiverUserIds: !Array.isArray(form?.feedbackGiverUserIds) || form.feedbackGiverUserIds.length === 0,
  };
}

function getExclusionValidationMessage(validation) {
  if (validation.incompleteReportIndexes.length > 0) {
    return 'Jeder angelegte Bericht an die Leitung der Höhenrettung benötigt Datum und Freitext.';
  }
  if (validation.outOfRangeReportIndexes.length > 0) {
    return 'Berichtszeitpunkte an die Leitung der Höhenrettung müssen zwischen Lehrgangsbeginn und Lehrgangsende liegen.';
  }
  if (validation.participantNotifiedOutOfRange) {
    return 'Das Mitteilungsdatum an den Teilnehmer muss zwischen Lehrgangsbeginn und Lehrgangsende liegen.';
  }
  if (validation.missingDetailedReason) {
    return 'Die ausführliche Begründung für den Ausschluss aus dem Lehrgang ist ein Pflichtfeld.';
  }
  if (validation.missingReports) {
    return 'Es muss mindestens ein Bericht an die Leitung der Höhenrettung dokumentiert werden.';
  }
  if (validation.missingHearingUsers) {
    return 'Es muss mindestens eine angehörte Person dokumentiert werden.';
  }
  if (validation.missingExclusionConfirmed) {
    return 'Die Bestätigung des Ausschlusses ist ein Pflichtfeld.';
  }
  if (validation.missingParticipantNotifiedDate) {
    return 'Das Mitteilungsdatum an den Teilnehmer ist ein Pflichtfeld.';
  }
  if (validation.participantNotifiedBeforeReports) {
    return 'Die Leitung der Höhenrettung muss vor der Mitteilung an den Teilnehmer informiert worden sein.';
  }
  return '';
}

function InstructorSafetyIncidentSection({
  safetyIncidentAnswer,
  onSafetyIncidentAnswerChange,
  isReadOnly,
  incidentFormState,
  onIncidentFieldChange,
  incidentOptions,
  incidentAttachmentFiles,
  onIncidentAttachmentFilesChange,
  selectedSafetyIncident,
  onDeleteLinkedIncidentAsset,
  deletingIncidentAssetId,
  busy,
}) {
  return (
    <>
      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Gab es sicherheitsrelevante Zwischenfälle?
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={safetyIncidentAnswer}
          onChange={(_, value) => {
            if (value) {
              onSafetyIncidentAnswerChange(value);
            }
          }}
          disabled={isReadOnly}
          fullWidth
        >
          <ToggleButton value={SRHT_SAFETY_ANSWER_YES}>Ja</ToggleButton>
          <ToggleButton value={SRHT_SAFETY_ANSWER_NO}>Nein</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          Die Antwort ist für die Finalisierung verpflichtend. Bei Ja wird der Zwischenfall direkt mit dem Rückmeldungsmodul verknüpft.
        </Typography>
      </Stack>

      {safetyIncidentAnswer === SRHT_SAFETY_ANSWER_YES ? (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Verknüpfter Zwischenfall
            </Typography>
            <IncidentFeedbackFormFields
              formState={incidentFormState}
              onFieldChange={onIncidentFieldChange}
              options={incidentOptions}
              attachmentFiles={incidentAttachmentFiles}
              onAttachmentFilesChange={onIncidentAttachmentFilesChange}
              showAssetPickers={!isReadOnly}
              disabled={isReadOnly}
            />

            {selectedSafetyIncident?.assets?.length ? (
              <Stack spacing={0.75}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Bereits gespeicherte Anhänge
                </Typography>
                {selectedSafetyIncident.assets.map((asset) => (
                  <Stack
                    key={asset.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Button
                      component="a"
                      href={asset.contentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="text"
                      sx={{ px: 0, justifyContent: 'flex-start' }}
                    >
                      {asset.originalName}
                    </Button>
                    {!isReadOnly ? (
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => onDeleteLinkedIncidentAsset(asset)}
                        disabled={busy || deletingIncidentAssetId === asset.id}
                      >
                        Entfernen
                      </Button>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {safetyIncidentAnswer === SRHT_SAFETY_ANSWER_NO && selectedSafetyIncident ? (
        <Alert severity="info">
          Der bereits angelegte Zwischenfall bleibt im Rückmeldungsmodul bestehen und wird beim nächsten Speichern nur vom Tagesfeedback entkoppelt.
        </Alert>
      ) : null}
    </>
  );
}

function SrhtAbsenceDecisionDialog({
  open,
  busy,
  participants,
  selections,
  onSelectionChange,
  onClose,
  onConfirm,
}) {
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md">
      <DialogTitle>Entscheidung wegen Fehlzeiten erforderlich</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert severity="warning">
            Mindestens ein Teilnehmer überschreitet nach diesem Tagesfeedback die Grenze von 12 Fehlstunden. Bitte legen Sie vor dem Speichern verbindlich fest, wie mit diesen Teilnehmern verfahren wird.
          </Alert>
          {participants.map((participant) => (
            <Paper key={`absence-decision-${participant.participantId}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {participant.fullName}
                  </Typography>
                  <Chip color="warning" label={`${formatAverage(participant.totalAbsenceHours)} h Fehlzeit`} />
                </Stack>
                <TextField
                  select
                  fullWidth
                  label="Verbindliche Entscheidung"
                  value={selections[String(participant.participantId)] || ''}
                  onChange={(event) => onSelectionChange(participant.participantId, event.target.value)}
                >
                  <MenuItem value=""><em>Auswählen</em></MenuItem>
                  {SRHT_ABSENCE_DECISION_OPTIONS.map((option) => (
                    <MenuItem key={`${participant.participantId}-${option.value}`} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Abbrechen</Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy}>
          Entscheidung übernehmen und speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LehrgangSrhtPage() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [courses, setCourses] = useState([]);
  const [referenceOptions, setReferenceOptions] = useState({
    assessmentEvents: [],
    dayInstructorOptions: [],
    weeklyFeedbackGiverOptions: [],
    permissions: {},
  });
  const [incidentOptions, setIncidentOptions] = useState({ entryTypes: [], responsibles: [] });
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [courseDetail, setCourseDetail] = useState(null);
  const [activeTab, setActiveTab] = useState(() => readPersistedTab(
    SRHT_COURSE_ACTIVE_TAB_STORAGE_KEY,
    SRHT_COURSE_ALLOWED_TABS,
    SRHT_TAB_MANAGE,
  ));
  const [selectedCandidateDayId, setSelectedCandidateDayId] = useState('');
  const [selectedInstructorDayId, setSelectedInstructorDayId] = useState('');
  const [isOverallEvaluationSelected, setIsOverallEvaluationSelected] = useState(true);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState(1);
  const [selectedParticipantId, setSelectedParticipantId] = useState('');
  const [candidateFeedbackForm, setCandidateFeedbackForm] = useState({ positiveText: '', negativeText: '', openQuestionsText: '' });
  const [instructorFeedbackForm, setInstructorFeedbackForm] = useState({ incompleteLessonsText: '', planningIssuesText: '', instructorUserIds: [], safetyIncidentAnswer: '' });
  const [incidentFormState, setIncidentFormState] = useState(() => getInitialIncidentFormState());
  const [incidentAttachmentFiles, setIncidentAttachmentFiles] = useState([]);
  const [deletingIncidentAssetId, setDeletingIncidentAssetId] = useState(null);
  const [dayReviewDrafts, setDayReviewDrafts] = useState({});
  const [evaluationForm, setEvaluationForm] = useState({
    decisionCode: '',
    noteText: '',
    feedbackText: '',
    feedbackCourseDayId: '',
    feedbackGiverUserIds: [],
  });
  const [instructorUnlockReason, setInstructorUnlockReason] = useState('');
  const [instructorWorkstepValidationVisible, setInstructorWorkstepValidationVisible] = useState(false);
  const [evaluationUnlockReason, setEvaluationUnlockReason] = useState('');
  const [evaluationValidationVisible, setEvaluationValidationVisible] = useState(false);
  const [selectedPdfExportType, setSelectedPdfExportType] = useState('course-report-all');
  const [selectedPdfParticipantIds, setSelectedPdfParticipantIds] = useState([]);
  const [weekOverview, setWeekOverview] = useState(null);
  const [isTrainingDayDialogOpen, setTrainingDayDialogOpen] = useState(false);
  const [exclusionParticipantId, setExclusionParticipantId] = useState('');
  const [exclusionForm, setExclusionForm] = useState(buildInitialSrhtExclusionForm(null));
  const [exclusionValidationVisible, setExclusionValidationVisible] = useState(false);
  const [exclusionValidationMode, setExclusionValidationMode] = useState('save');
  const [exclusionConfirmDialogOpen, setExclusionConfirmDialogOpen] = useState(false);
  const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false);
  const [exclusionUnlockDialogOpen, setExclusionUnlockDialogOpen] = useState(false);
  const [exclusionUnlockReason, setExclusionUnlockReason] = useState('');
  const [absenceDecisionDialogOpen, setAbsenceDecisionDialogOpen] = useState(false);
  const [pendingInstructorSubmitMode, setPendingInstructorSubmitMode] = useState('save');
  const [absenceDecisionParticipants, setAbsenceDecisionParticipants] = useState([]);
  const [absenceDecisionSelections, setAbsenceDecisionSelections] = useState({});
  const [courseAdminForm, setCourseAdminForm] = useState(buildCourseAdminForm(null));
  const [courseUnlockReason, setCourseUnlockReason] = useState('');
  const [closeCourseDialogOpen, setCloseCourseDialogOpen] = useState(false);
  const [unlockCourseDialogOpen, setUnlockCourseDialogOpen] = useState(false);
  const [deleteCourseDialogOpen, setDeleteCourseDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    label: '',
    startDate: '',
    endDate: '',
    notes: '',
    totalHours: 120,
    trainingDays: [],
    assessmentEventIds: [],
    participantUserIds: [],
  });

  const permissions = useMemo(
    () => ({
      ...(referenceOptions.permissions || {}),
      ...(courseDetail?.permissions || {}),
    }),
    [courseDetail?.permissions, referenceOptions.permissions],
  );
  const selectedCourse = courseDetail?.course || null;
  const isSelectedCourseLocked = selectedCourse?.status === 'closed' || selectedCourse?.status === 'archived';
  const visibleTabs = useMemo(() => getVisibleSrhtTabs(permissions), [permissions]);
  const resolvedActiveTab = visibleTabs.includes(activeTab) ? activeTab : visibleTabs[0] || SRHT_TAB_CANDIDATE;
  const trainingDayCalendarMonths = useMemo(() => (
    buildSrhtCourseDayCalendarMonths(createForm.startDate, createForm.endDate, createForm.trainingDays)
  ), [createForm.endDate, createForm.startDate, createForm.trainingDays]);
  const isTrainingDayRangeReady = trainingDayCalendarMonths.length > 0;

  const loadInitialData = async () => {
    setLoading(true);
    setError('');

    try {
      const [referenceResponse, coursesResponse] = await Promise.all([
        getSrhtCourseReferenceOptions(),
        getSrhtCourses(),
      ]);

      const nextReferenceOptions = referenceResponse.data || { assessmentEvents: [], dayInstructorOptions: [], weeklyFeedbackGiverOptions: [], permissions: {} };
      setReferenceOptions(nextReferenceOptions);
      if (nextReferenceOptions.permissions?.canEditInstructorFeedback) {
        try {
          const feedbackOptionsResponse = await getFeedbackOptions();
          setIncidentOptions(feedbackOptionsResponse.data || { entryTypes: [], responsibles: [] });
        } catch {
          setIncidentOptions({ entryTypes: [], responsibles: [] });
        }
      } else {
        setIncidentOptions({ entryTypes: [], responsibles: [] });
      }
      const nextCourses = coursesResponse.data?.courses || [];
      setCourses(nextCourses);
      setSelectedCourseId((currentValue) => {
        if (currentValue && nextCourses.some((course) => String(course.id) === String(currentValue))) {
          return currentValue;
        }
        return nextCourses[0]?.id ? String(nextCourses[0].id) : '';
      });
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Lehrgänge konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const loadCourseDetail = async (courseId) => {
    if (!courseId) {
      setCourseDetail(null);
      return null;
    }

    try {
      const response = await getSrhtCourse(courseId);
      const nextDetail = response.data || null;
      setCourseDetail(nextDetail);
      return nextDetail;
    } catch (loadError) {
      setCourseDetail(null);
      setError(loadError.response?.data?.error || 'Lehrgangsdetails konnten nicht geladen werden.');
      return null;
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadCourseDetail(selectedCourseId);
  }, [selectedCourseId]);

  useEffect(() => {
    if (activeTab !== resolvedActiveTab) {
      setActiveTab(resolvedActiveTab);
    }
  }, [activeTab, resolvedActiveTab]);

  useEffect(() => {
    writePersistedTab(SRHT_COURSE_ACTIVE_TAB_STORAGE_KEY, resolvedActiveTab, SRHT_COURSE_ALLOWED_TABS);
  }, [resolvedActiveTab]);

  useEffect(() => {
    if (!selectedCourseId || !courseDetail || isOverallEvaluationSelected) {
      setWeekOverview(null);
      return;
    }

    getSrhtWeekOverview(selectedCourseId, selectedWeekNumber)
      .then((response) => setWeekOverview(response.data || null))
      .catch(() => setWeekOverview(null));
  }, [selectedCourseId, selectedWeekNumber, courseDetail, isOverallEvaluationSelected]);

  useEffect(() => {
    setCreateForm((currentValue) => {
      const filteredTrainingDays = currentValue.trainingDays.filter((value) => (
        isIsoDateWithinRange(value, currentValue.startDate, currentValue.endDate)
      ));

      if (filteredTrainingDays.length === currentValue.trainingDays.length) {
        return currentValue;
      }

      return {
        ...currentValue,
        trainingDays: filteredTrainingDays,
      };
    });
  }, [createForm.endDate, createForm.startDate]);

  useEffect(() => {
    const firstDayId = courseDetail?.days?.[0]?.id ? String(courseDetail.days[0].id) : '';
    if (!selectedCandidateDayId && firstDayId) {
      setSelectedCandidateDayId(firstDayId);
    }
    if (selectedCandidateDayId && !courseDetail?.days?.some((day) => String(day.id) === String(selectedCandidateDayId))) {
      setSelectedCandidateDayId(firstDayId);
    }
    if (!selectedInstructorDayId && firstDayId) {
      setSelectedInstructorDayId(firstDayId);
    }
    if (selectedInstructorDayId && !courseDetail?.days?.some((day) => String(day.id) === String(selectedInstructorDayId))) {
      setSelectedInstructorDayId(firstDayId);
    }
  }, [courseDetail, selectedCandidateDayId, selectedInstructorDayId]);

  useEffect(() => {
    const firstParticipantId = courseDetail?.participants?.[0]?.id ? String(courseDetail.participants[0].id) : '';
    if (selectedParticipantId && !courseDetail?.participants?.some((participant) => String(participant.id) === String(selectedParticipantId))) {
      setSelectedParticipantId(firstParticipantId);
    }
  }, [courseDetail, selectedParticipantId]);

  useEffect(() => {
    const validParticipantIds = new Set((courseDetail?.participants || []).map((participant) => String(participant.id)));
    setSelectedPdfParticipantIds((currentValue) => currentValue.filter((participantId) => validParticipantIds.has(String(participantId))));
  }, [courseDetail?.participants]);

  const selectedCandidateFeedbackEntry = useMemo(() => (
    (courseDetail?.candidateFeedbackEntries || []).find((entry) => String(entry.course_day_id) === String(selectedCandidateDayId)) || null
  ), [courseDetail, selectedCandidateDayId]);

  const selectedInstructorFeedbackEntry = useMemo(() => (
    (courseDetail?.instructorFeedbackEntries || []).find((entry) => String(entry.course_day_id) === String(selectedInstructorDayId)) || null
  ), [courseDetail, selectedInstructorDayId]);
  const selectedInstructorDay = useMemo(() => (
    (courseDetail?.days || []).find((day) => String(day.id) === String(selectedInstructorDayId)) || null
  ), [courseDetail?.days, selectedInstructorDayId]);
  const isInstructorWorkstepFinalized = selectedInstructorFeedbackEntry?.status === 'finalized';
  const isInstructorWorkstepReadOnly = !permissions.canEditInstructorFeedback || isSelectedCourseLocked || isInstructorWorkstepFinalized;
  const selectedSafetyIncident = selectedInstructorFeedbackEntry?.safetyIncident || null;

  const selectedDayInstructorIds = useMemo(() => (
    (courseDetail?.dayInstructors || [])
      .filter((item) => String(item.course_day_id) === String(selectedInstructorDayId))
      .map((item) => Number(item.user_id))
  ), [courseDetail, selectedInstructorDayId]);

  const selectedDayReviews = useMemo(() => (
    (courseDetail?.reviews || []).filter((review) => String(review.course_day_id) === String(selectedInstructorDayId))
  ), [courseDetail, selectedInstructorDayId]);
  const courseDayIndexById = useMemo(
    () => new Map((courseDetail?.days || []).map((day) => [String(day.id), Number(day.training_day_index || 0)])),
    [courseDetail?.days],
  );
  const selectedInstructorDayRequiresReviews = useMemo(
    () => doesInstructorDayRequireParticipantReviews(selectedInstructorDayId, courseDayIndexById),
    [courseDayIndexById, selectedInstructorDayId],
  );
  const instructorDayTiles = useMemo(() => {
    const dayByTrainingIndex = new Map((courseDetail?.days || []).map((day) => [Number(day.training_day_index), day]));
    const feedbackByDayId = new Map((courseDetail?.instructorFeedbackEntries || []).map((entry) => [Number(entry.course_day_id), entry]));

    return Array.from({ length: SRHT_INSTRUCTOR_DAY_TILE_COUNT }, (_, index) => {
      const trainingDayIndex = index + 1;
      const day = dayByTrainingIndex.get(trainingDayIndex) || null;
      const feedbackEntry = day ? feedbackByDayId.get(Number(day.id)) || null : null;
      const status = !day ? 'missing' : feedbackEntry?.status === 'finalized' ? 'finalized' : 'open';

      return {
        trainingDayIndex,
        dayId: day ? String(day.id) : '',
        status,
        dateLabel: day?.course_date ? formatDate(day.course_date) : '',
      };
    });
  }, [courseDetail]);
  const relevantInstructorParticipants = useMemo(
    () => getRelevantInstructorParticipants(courseDetail?.participants || [], selectedInstructorDayId, courseDayIndexById),
    [courseDetail?.participants, courseDayIndexById, selectedInstructorDayId],
  );
  const instructorWorkstepValidation = useMemo(
    () => buildInstructorWorkstepValidation({
      instructorUserIds: instructorFeedbackForm.instructorUserIds,
      dayReviewDrafts,
      participants: relevantInstructorParticipants,
    }),
    [dayReviewDrafts, instructorFeedbackForm.instructorUserIds, relevantInstructorParticipants],
  );

  const selectedParticipant = useMemo(() => (
    (courseDetail?.participants || []).find((participant) => String(participant.id) === String(selectedParticipantId)) || null
  ), [courseDetail, selectedParticipantId]);
  const selectedCoursePdfOption = useMemo(
    () => COURSE_PDF_OPTIONS.find((option) => option.value === selectedPdfExportType) || COURSE_PDF_OPTIONS[0],
    [selectedPdfExportType],
  );
  const coursePdfParticipantNameById = useMemo(
    () => new Map((courseDetail?.participants || []).map((participant) => [String(participant.id), participant.full_name || '-'])),
    [courseDetail?.participants],
  );
  const exclusionParticipant = useMemo(() => (
    (courseDetail?.participants || []).find((participant) => String(participant.id) === String(exclusionParticipantId)) || null
  ), [courseDetail, exclusionParticipantId]);
  const selectedExclusionRecord = exclusionParticipant?.exclusionRecord || null;
  const canManageExclusions = Boolean(permissions.canManageExclusions);
  const isExclusionFinalized = selectedExclusionRecord?.status === 'finalized';
  const isExclusionReadOnly = !canManageExclusions || isSelectedCourseLocked || isExclusionFinalized;
  const exclusionWeeklySummaries = useMemo(
    () => (exclusionParticipant?.weeklySummaries || []).filter((item) => item?.storedEvaluation?.status === 'finalized'),
    [exclusionParticipant],
  );
  const exclusionValidation = useMemo(
    () => validateSrhtExclusionForm(exclusionForm, selectedCourse || {}, { requireFinalize: exclusionValidationMode === 'finalize' }),
    [exclusionForm, exclusionValidationMode, selectedCourse],
  );

  const selectedWeeklySummary = useMemo(() => (
    selectedParticipant?.weeklySummaries?.find((item) => Number(item.weekNumber) === Number(selectedWeekNumber)) || null
  ), [selectedParticipant, selectedWeekNumber]);
  const selectedOverallSummary = useMemo(() => getParticipantOverallSummary(selectedParticipant), [selectedParticipant]);
  const evaluationParticipantEntries = useMemo(() => {
    if (isOverallEvaluationSelected) {
      return (courseDetail?.participants || []).map((participant) => ({
        participantId: participant.id,
        userId: participant.user_id,
        fullName: participant.full_name,
        status: participant.status,
        withdrawn_on_day_id: participant.withdrawn_on_day_id,
        summary: getParticipantOverallSummary(participant),
      }));
    }

    return weekOverview?.participants || [];
  }, [courseDetail?.participants, isOverallEvaluationSelected, weekOverview?.participants]);
  const isWeeklyEvaluationFinalized = selectedWeeklySummary?.storedEvaluation?.status === 'finalized';
  const isWeeklyEvaluationReadOnly = !permissions.canEditEvaluations || isSelectedCourseLocked || isWeeklyEvaluationFinalized;
  const weeklyEvaluationValidation = useMemo(
    () => buildWeeklyEvaluationValidation(evaluationForm),
    [evaluationForm],
  );

  useEffect(() => {
    setCandidateFeedbackForm({
      positiveText: selectedCandidateFeedbackEntry?.positive_text || '',
      negativeText: selectedCandidateFeedbackEntry?.negative_text || '',
      openQuestionsText: selectedCandidateFeedbackEntry?.open_questions_text || '',
    });
  }, [selectedCandidateFeedbackEntry]);

  useEffect(() => {
    setInstructorFeedbackForm({
      incompleteLessonsText: selectedInstructorFeedbackEntry?.incomplete_lessons_text || '',
      planningIssuesText: selectedInstructorFeedbackEntry?.planning_issues_text || '',
      instructorUserIds: selectedDayInstructorIds,
      safetyIncidentAnswer: selectedInstructorFeedbackEntry?.safetyIncidentAnswer || '',
    });
    setIncidentFormState(buildInitialInstructorIncidentForm(selectedInstructorDay?.course_date || '', selectedInstructorFeedbackEntry?.safetyIncident || null));
    setIncidentAttachmentFiles([]);
    setInstructorWorkstepValidationVisible(false);
    setInstructorUnlockReason('');
    setAbsenceDecisionDialogOpen(false);
    setAbsenceDecisionParticipants([]);
    setAbsenceDecisionSelections({});
  }, [selectedInstructorDay?.course_date, selectedInstructorFeedbackEntry, selectedDayInstructorIds]);

  useEffect(() => {
    const nextDrafts = {};
    (courseDetail?.participants || []).forEach((participant) => {
      const existingReview = selectedDayReviews.find((review) => Number(review.course_participant_id) === Number(participant.id));
      nextDrafts[participant.id] = {
        participantId: participant.id,
        knowledgeScore: existingReview?.knowledge_score ?? '',
        singleMeasuresScore: existingReview?.single_measures_score ?? '',
        independenceScore: existingReview?.independence_score ?? '',
        errorManagementScore: existingReview?.error_management_score ?? '',
        teamworkScore: existingReview?.teamwork_score ?? '',
        remarkText: existingReview?.remark_text || '',
        absenceHours: existingReview?.absence_hours ?? 0,
      };
    });
    setDayReviewDrafts(nextDrafts);
  }, [courseDetail, selectedDayReviews]);

  useEffect(() => {
    const storedEvaluation = selectedWeeklySummary?.storedEvaluation || null;
    setEvaluationForm({
      decisionCode: storedEvaluation?.decision_code || '',
      noteText: storedEvaluation?.note_text || '',
      feedbackText: storedEvaluation?.feedback_text || '',
      feedbackCourseDayId: storedEvaluation?.feedback_course_day_id ? String(storedEvaluation.feedback_course_day_id) : '',
      feedbackGiverUserIds: (storedEvaluation?.feedbackGivers || []).map((item) => Number(item.userId)),
    });
    setEvaluationUnlockReason('');
    setEvaluationValidationVisible(false);
  }, [selectedWeeklySummary]);

  useEffect(() => {
    setCourseAdminForm(buildCourseAdminForm(courseDetail?.course || null));
    setCourseUnlockReason('');
  }, [courseDetail]);

  useEffect(() => {
    setExclusionForm(buildInitialSrhtExclusionForm(selectedExclusionRecord));
    setExclusionValidationVisible(false);
    setExclusionValidationMode('save');
    setExclusionUnlockReason('');
  }, [selectedExclusionRecord]);

  const selectedAssessmentParticipantOptions = useMemo(() => {
    const optionMap = new Map();
    (referenceOptions.assessmentEvents || [])
      .filter((event) => createForm.assessmentEventIds.includes(Number(event.id)))
      .forEach((event) => {
        (event.qualifiedCandidates || []).forEach((candidate) => {
          const userId = Number(candidate.userId);
          if (!optionMap.has(userId)) {
            optionMap.set(userId, {
              userId,
              fullName: candidate.fullName || '-',
              candidateRoleVariant: candidate.candidateRoleVariant || 'bewerber_srht',
              eventLabels: [],
            });
          }
          optionMap.get(userId).eventLabels.push(event.label || `Eignungsverfahren ${event.id}`);
        });
      });

    return [...optionMap.values()].sort((left, right) => left.fullName.localeCompare(right.fullName, 'de'));
  }, [referenceOptions, createForm.assessmentEventIds]);

  const handleCourseRefresh = async (courseId = selectedCourseId) => {
    await loadInitialData();
    if (courseId) {
      await loadCourseDetail(courseId);
    }
  };

  const handleCreateCourse = async () => {
    setBusy(true);
    setError('');
    setSuccess('');

    try {
      const response = await createSrhtCourse({
        label: createForm.label,
        startDate: createForm.startDate,
        endDate: createForm.endDate,
        notes: createForm.notes,
        totalHours: Number(createForm.totalHours || 120),
        trainingDays: createForm.trainingDays,
        assessmentEventIds: createForm.assessmentEventIds,
        participantUserIds: createForm.participantUserIds,
      });

      const createdCourseId = String(response.data?.course?.id || response.data?.courseId || '');
      setSuccess('Lehrgang wurde angelegt.');
      setCreateForm({
        label: '',
        startDate: '',
        endDate: '',
        notes: '',
        totalHours: 120,
        trainingDays: [],
        assessmentEventIds: [],
        participantUserIds: [],
      });
      await handleCourseRefresh(createdCourseId);
      if (createdCourseId) {
        setSelectedCourseId(createdCourseId);
      }
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Lehrgang konnte nicht angelegt werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateCourseMasterData = async () => {
    if (!selectedCourseId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await updateSrhtCourse(selectedCourseId, {
        label: courseAdminForm.label,
        startDate: courseAdminForm.startDate,
        endDate: courseAdminForm.endDate,
        notes: courseAdminForm.notes,
        totalHours: Number(courseAdminForm.totalHours || 120),
      });
      await handleCourseRefresh(selectedCourseId);
      setSuccess('Lehrgangs-Stammdaten gespeichert.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Lehrgangs-Stammdaten konnten nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleCloseCourse = async () => {
    if (!selectedCourseId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await closeSrhtCourse(selectedCourseId);
      await handleCourseRefresh(selectedCourseId);
      setCloseCourseDialogOpen(false);
      setSuccess('Der Lehrgang wurde abgeschlossen. Änderungen sind jetzt erst nach einer Wiederöffnung mit Begründung möglich.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Lehrgang konnte nicht abgeschlossen werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlockCourse = async () => {
    if (!selectedCourseId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await unlockSrhtCourse(selectedCourseId, { reason: courseUnlockReason });
      await handleCourseRefresh(selectedCourseId);
      setUnlockCourseDialogOpen(false);
      setCourseUnlockReason('');
      setSuccess('Der Lehrgang wurde wieder geöffnet.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Lehrgang konnte nicht wieder geöffnet werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourseId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await deleteSrhtCourse(selectedCourseId);
      setDeleteCourseDialogOpen(false);
      await loadInitialData();
      setCourseDetail(null);
      setSuccess('Der Lehrgang wurde gelöscht.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Lehrgang konnte nicht gelöscht werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleCandidateFeedbackSave = async () => {
    if (!selectedCourseId || !selectedCandidateDayId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await saveSrhtCandidateFeedback(selectedCourseId, selectedCandidateDayId, candidateFeedbackForm);
      await loadCourseDetail(selectedCourseId);
      setSuccess('Teilnehmerfeedback gespeichert.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Teilnehmerfeedback konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleCandidateFeedbackFinalize = async () => {
    if (!selectedCourseId || !selectedCandidateDayId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await finalizeSrhtCandidateFeedback(selectedCourseId, selectedCandidateDayId);
      await loadCourseDetail(selectedCourseId);
      setSuccess('Teilnehmerfeedback finalisiert.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Teilnehmerfeedback konnte nicht finalisiert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleCandidateFeedbackUnlock = async () => {
    if (!selectedCourseId || !selectedCandidateDayId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await unlockSrhtCandidateFeedback(selectedCourseId, selectedCandidateDayId);
      await loadCourseDetail(selectedCourseId);
      setSuccess('Teilnehmerfeedback entsperrt.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Teilnehmerfeedback konnte nicht entsperrt werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleInstructorFeedbackUnlock = async () => {
    if (!selectedCourseId || !selectedInstructorDayId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await unlockSrhtInstructorFeedback(selectedCourseId, selectedInstructorDayId, { reason: instructorUnlockReason });
      await loadCourseDetail(selectedCourseId);
      setSuccess(selectedInstructorDayRequiresReviews ? 'Lehrhöhenretter-Tagesfeedback und Tagesbewertungen entsperrt.' : 'Lehrhöhenretter-Tagesfeedback entsperrt.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Der gemeinsame Tagesbogen konnte nicht entsperrt werden.');
    } finally {
      setBusy(false);
    }
  };

  const buildInstructorWorkstepPayload = () => ({
    incompleteLessonsText: instructorFeedbackForm.incompleteLessonsText,
    planningIssuesText: instructorFeedbackForm.planningIssuesText,
    instructorUserIds: instructorFeedbackForm.instructorUserIds,
    safetyIncidentAnswer: instructorFeedbackForm.safetyIncidentAnswer || null,
    safetyIncident: instructorFeedbackForm.safetyIncidentAnswer === SRHT_SAFETY_ANSWER_YES
      ? (() => {
        const incidentPayload = buildIncidentSubmitPayload(incidentFormState, incidentOptions);
        return {
          linkedFeedbackEntryId: selectedSafetyIncident?.id || null,
          eventDate: incidentPayload.eventDate,
          title: incidentPayload.title,
          description: incidentPayload.description,
          payload: incidentPayload.payload,
        };
      })()
      : null,
    reviews: selectedInstructorDayRequiresReviews ? relevantInstructorParticipants.map((participant) => {
      const draft = dayReviewDrafts[participant.id] || { participantId: participant.id };
      return {
        ...draft,
        participantId: participant.id,
        absenceHours: draft.absenceHours === '' || draft.absenceHours === null || typeof draft.absenceHours === 'undefined'
          ? 0
          : Number(draft.absenceHours),
      };
    }) : [],
  });

  const validateInstructorIncidentBeforeSubmit = (requireFinalize) => {
    const validationMessage = getInstructorIncidentValidationMessage({
      requireFinalize,
      safetyIncidentAnswer: instructorFeedbackForm.safetyIncidentAnswer,
      incidentFormState,
    });

    if (validationMessage) {
      setError(validationMessage);
      return false;
    }

    return true;
  };

  const resetAbsenceDecisionDialog = () => {
    setAbsenceDecisionDialogOpen(false);
    setPendingInstructorSubmitMode('save');
    setAbsenceDecisionParticipants([]);
    setAbsenceDecisionSelections({});
  };

  const submitInstructorFeedback = async (submitMode, absenceDecisions = []) => {
    if (!selectedCourseId || !selectedInstructorDayId) return null;
    setError('');
    setSuccess('');

    if (!validateInstructorWorkstepBeforeSubmit(submitMode === 'finalize')) {
      return null;
    }

    if (!validateInstructorIncidentBeforeSubmit(submitMode === 'finalize')) {
      return null;
    }

    setBusy(true);
    try {
      await saveSrhtInstructorFeedback(selectedCourseId, selectedInstructorDayId, {
        ...buildInstructorWorkstepPayload(),
        absenceDecisions,
      });

      let nextDetail = await loadCourseDetail(selectedCourseId);
      const savedIncidentId = nextDetail?.instructorFeedbackEntries
        ?.find((entry) => String(entry.course_day_id) === String(selectedInstructorDayId))
        ?.safetyIncident?.id;

      if (savedIncidentId && incidentAttachmentFiles.length > 0) {
        await uploadFeedbackAssets(savedIncidentId, [...incidentAttachmentFiles]);
        nextDetail = await loadCourseDetail(selectedCourseId);
      }

      if (submitMode === 'finalize') {
        await finalizeSrhtInstructorFeedback(selectedCourseId, selectedInstructorDayId);
        nextDetail = await loadCourseDetail(selectedCourseId);
      }

      setIncidentAttachmentFiles([]);
      resetAbsenceDecisionDialog();
      setSuccess(
        submitMode === 'finalize'
          ? (selectedInstructorDayRequiresReviews ? 'Lehrhöhenretter-Tagesfeedback und Tagesbewertungen finalisiert.' : 'Lehrhöhenretter-Tagesfeedback finalisiert.')
          : (selectedInstructorDayRequiresReviews ? 'Lehrhöhenretter-Tagesfeedback und Tagesbewertungen gespeichert.' : 'Lehrhöhenretter-Tagesfeedback gespeichert.'),
      );
      return nextDetail;
    } catch (actionError) {
      const errorCode = actionError.response?.data?.code;
      const requiredParticipants = actionError.response?.data?.details?.participants;
      if (errorCode === 'srht_absence_decisions_required' && Array.isArray(requiredParticipants) && requiredParticipants.length > 0) {
        setPendingInstructorSubmitMode(submitMode);
        setAbsenceDecisionParticipants(requiredParticipants);
        setAbsenceDecisionSelections(Object.fromEntries(requiredParticipants.map((participant) => [String(participant.participantId), ''])));
        setAbsenceDecisionDialogOpen(true);
        return null;
      }

      setError(actionError.response?.data?.error || 'Der gemeinsame Tagesbogen konnte nicht gespeichert werden.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const validateInstructorWorkstepBeforeSubmit = (requireFinalize = false) => {
    setInstructorWorkstepValidationVisible(requireFinalize);

    if (!selectedInstructorDayId) {
      setError('Bitte zuerst einen Lehrgangstag auswählen.');
      return false;
    }

    if (!requireFinalize) {
      return true;
    }

    if (instructorWorkstepValidation.missingInstructorSelection) {
      setError('Das Feld "Durchführende Lehrhöhenretter" ist ein Pflichtfeld.');
      return false;
    }

    if (instructorWorkstepValidation.missingParticipantEntries.length > 0) {
      const firstMissingEntry = instructorWorkstepValidation.missingParticipantEntries[0];
      setError(`Für ${firstMissingEntry.fullName} fehlen Pflichtfelder: ${firstMissingEntry.missingLabels.join(', ')}.`);
      return false;
    }

    return true;
  };

  const handleInstructorFeedbackSave = async () => {
    await submitInstructorFeedback('save');
  };

  const handleInstructorFeedbackFinalize = async () => {
    await submitInstructorFeedback('finalize');
  };

  const handleDayReviewChange = (participantId, patch) => {
    setDayReviewDrafts((currentValue) => ({
      ...currentValue,
      [participantId]: {
        ...(currentValue[participantId] || {}),
        ...patch,
      },
    }));
  };

  const handleInstructorIncidentFieldChange = (fieldName, value) => {
    setIncidentFormState((currentValue) => ({
      ...currentValue,
      [fieldName]: value,
    }));
  };

  const handleDeleteLinkedIncidentAsset = async (asset) => {
    if (!selectedCourseId || !selectedSafetyIncident?.id || !asset?.id) {
      return;
    }

    try {
      setDeletingIncidentAssetId(asset.id);
      setError('');
      setSuccess('');
      await deleteFeedbackAsset(selectedSafetyIncident.id, asset.id);
      await loadCourseDetail(selectedCourseId);
      setSuccess('Anhang des verknüpften Zwischenfalls entfernt.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Anhang des verknüpften Zwischenfalls konnte nicht entfernt werden.');
    } finally {
      setDeletingIncidentAssetId(null);
    }
  };

  const handleConfirmAbsenceDecisions = async () => {
    const missingDecisions = absenceDecisionParticipants.some(
      (participant) => !String(absenceDecisionSelections[String(participant.participantId)] || '').trim(),
    );

    if (missingDecisions) {
      setError('Bitte für jeden betroffenen Teilnehmer eine Entscheidung auswählen.');
      return;
    }

    await submitInstructorFeedback(
      pendingInstructorSubmitMode,
      absenceDecisionParticipants.map((participant) => ({
        participantId: Number(participant.participantId),
        decisionCode: absenceDecisionSelections[String(participant.participantId)],
      })),
    );
  };

  const handleSaveWeeklyEvaluation = async () => {
    if (!selectedCourseId || !selectedParticipantId) return;
    setError('');
    setSuccess('');
    setEvaluationValidationVisible(true);

    if (weeklyEvaluationValidation.missingDecisionCode) {
      setError('Die Einschätzung der Lehrgangsleitung ist ein Pflichtfeld.');
      return;
    }
    if (weeklyEvaluationValidation.missingFeedbackText) {
      setError('Das Feld Feedback ist ein Pflichtfeld.');
      return;
    }
    if (weeklyEvaluationValidation.missingFeedbackCourseDayId) {
      setError('Das Feld Feedback an Tag ist ein Pflichtfeld.');
      return;
    }
    if (weeklyEvaluationValidation.missingFeedbackGiverUserIds) {
      setError('Das Feld Feedback gegeben durch ist ein Pflichtfeld.');
      return;
    }

    setBusy(true);
    try {
      await saveSrhtWeeklyEvaluation(selectedCourseId, selectedParticipantId, selectedWeekNumber, {
        decisionCode: evaluationForm.decisionCode,
        noteText: evaluationForm.noteText,
        feedbackText: evaluationForm.feedbackText,
        feedbackCourseDayId: evaluationForm.feedbackCourseDayId ? Number(evaluationForm.feedbackCourseDayId) : null,
        feedbackGiverUserIds: evaluationForm.feedbackGiverUserIds,
      });
      await loadCourseDetail(selectedCourseId);
      setSuccess('Wochenauswertung gespeichert.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Wochenauswertung konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleFinalizeWeeklyEvaluation = async () => {
    if (!selectedCourseId || !selectedParticipantId) return;
    setError('');
    setSuccess('');
    setEvaluationValidationVisible(true);

    if (weeklyEvaluationValidation.missingDecisionCode) {
      setError('Die Einschätzung der Lehrgangsleitung ist ein Pflichtfeld.');
      return;
    }
    if (weeklyEvaluationValidation.missingFeedbackText) {
      setError('Das Feld Feedback ist ein Pflichtfeld.');
      return;
    }
    if (weeklyEvaluationValidation.missingFeedbackCourseDayId) {
      setError('Das Feld Feedback an Tag ist ein Pflichtfeld.');
      return;
    }
    if (weeklyEvaluationValidation.missingFeedbackGiverUserIds) {
      setError('Das Feld Feedback gegeben durch ist ein Pflichtfeld.');
      return;
    }

    setBusy(true);
    try {
      await saveSrhtWeeklyEvaluation(selectedCourseId, selectedParticipantId, selectedWeekNumber, {
        decisionCode: evaluationForm.decisionCode,
        noteText: evaluationForm.noteText,
        feedbackText: evaluationForm.feedbackText,
        feedbackCourseDayId: evaluationForm.feedbackCourseDayId ? Number(evaluationForm.feedbackCourseDayId) : null,
        feedbackGiverUserIds: evaluationForm.feedbackGiverUserIds,
      });
      await finalizeSrhtWeeklyEvaluation(selectedCourseId, selectedParticipantId, selectedWeekNumber);
      await loadCourseDetail(selectedCourseId);
      setSuccess('Wochenauswertung finalisiert.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Wochenauswertung konnte nicht finalisiert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlockWeeklyEvaluation = async () => {
    if (!selectedCourseId || !selectedParticipantId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await unlockSrhtWeeklyEvaluation(selectedCourseId, selectedParticipantId, selectedWeekNumber, {
        reason: evaluationUnlockReason,
      });
      await loadCourseDetail(selectedCourseId);
      setSuccess('Wochenauswertung entsperrt.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Wochenauswertung konnte nicht entsperrt werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedCourseId) return;

    const participantIds = selectedPdfParticipantIds
      .map((participantId) => Number(participantId))
      .filter((participantId) => Number.isInteger(participantId) && participantId > 0);

    if (selectedCoursePdfOption.requiresParticipantSelection && !participantIds.length) {
      setError('Bitte zuerst mindestens einen Teilnehmer für den PDF-Export auswählen.');
      return;
    }

    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const participantGroups = selectedCoursePdfOption.singlePdfPerParticipant
        ? participantIds.map((participantId) => [participantId])
        : [selectedCoursePdfOption.requiresParticipantSelection ? participantIds : []];

      for (const scopedParticipantIds of participantGroups) {
        const response = await exportSrhtCoursePdf(selectedCourseId, {
          section: selectedCoursePdfOption.section,
          participantIds: scopedParticipantIds,
        });
        downloadPdfResponse(response, 'Lehrgang-SRHT.pdf');
      }

      setSuccess(
        selectedCoursePdfOption.singlePdfPerParticipant
          ? `${participantGroups.length} Teilnehmer-PDFs wurden erstellt.`
          : 'Lehrgangs-PDF wurde erstellt.',
      );
    } catch (actionError) {
      setError(await extractPdfErrorMessage(actionError, 'Lehrgangs-PDF konnte nicht erstellt werden.'));
    } finally {
      setBusy(false);
    }
  };

  const handleOpenTrainingDayDialog = () => {
    if (!isTrainingDayRangeReady) {
      setError('Bitte zuerst einen gültigen Zeitraum mit Start- und Enddatum festlegen.');
      return;
    }

    setError('');
    setTrainingDayDialogOpen(true);
  };

  const handleToggleTrainingDay = (isoDate) => {
    setCreateForm((currentValue) => ({
      ...currentValue,
      trainingDays: toggleIsoDateSelection(currentValue.trainingDays, isoDate),
    }));
  };

  const openExclusionDialogForParticipant = (participant) => {
    if (!participant) {
      return;
    }

    setExclusionParticipantId(String(participant.id));
    setExclusionValidationVisible(false);
    setExclusionUnlockReason('');
    if (participant.exclusionRecord) {
      setExclusionDialogOpen(true);
      return;
    }

    setExclusionConfirmDialogOpen(true);
  };

  const handleConfirmExclusionStart = () => {
    setExclusionConfirmDialogOpen(false);
    setExclusionDialogOpen(true);
  };

  const handleCloseExclusionDialog = () => {
    setExclusionDialogOpen(false);
    setExclusionValidationVisible(false);
    setExclusionValidationMode('save');
    setExclusionUnlockDialogOpen(false);
    setExclusionUnlockReason('');
  };

  const handleExclusionReportChange = (index, patch) => {
    setExclusionForm((currentValue) => ({
      ...currentValue,
      reports: currentValue.reports.map((report, reportIndex) => (
        reportIndex === index
          ? { ...report, ...patch }
          : report
      )),
    }));
  };

  const handleAddExclusionReport = () => {
    setExclusionForm((currentValue) => {
      if ((currentValue.reports || []).length >= SRHT_EXCLUSION_MAX_REPORTS) {
        return currentValue;
      }

      return {
        ...currentValue,
        reports: [...(currentValue.reports || []), buildEmptySrhtExclusionReport((currentValue.reports || []).length)],
      };
    });
  };

  const handleRemoveExclusionReport = (index) => {
    setExclusionForm((currentValue) => {
      const nextReports = (currentValue.reports || []).filter((_, reportIndex) => reportIndex !== index);
      return {
        ...currentValue,
        reports: nextReports.length > 0 ? nextReports : [buildEmptySrhtExclusionReport(0)],
      };
    });
  };

  const validateExclusionForAction = (requireFinalize) => {
    setExclusionValidationMode(requireFinalize ? 'finalize' : 'save');
    const validation = validateSrhtExclusionForm(exclusionForm, selectedCourse || {}, { requireFinalize });
    setExclusionValidationVisible(true);

    const blockingValidation = requireFinalize
      ? validation
      : {
        ...validation,
        missingDetailedReason: false,
        missingReports: false,
        missingHearingUsers: false,
        missingExclusionConfirmed: false,
        missingParticipantNotifiedDate: false,
        hasErrors: validation.incompleteReportIndexes.length > 0
          || validation.outOfRangeReportIndexes.length > 0
          || validation.participantNotifiedOutOfRange
          || validation.participantNotifiedBeforeReports,
      };

    if (blockingValidation.hasErrors) {
      setError(getExclusionValidationMessage(blockingValidation));
      return false;
    }

    return true;
  };

  const buildExclusionPayload = () => ({
    detailedReasonText: exclusionForm.detailedReasonText,
    reports: exclusionForm.reports,
    hearingUserIds: exclusionForm.hearingUserIds,
    exclusionConfirmed: exclusionForm.exclusionConfirmed,
    participantNotifiedDate: exclusionForm.participantNotifiedDate,
  });

  const handleSaveExclusion = async () => {
    if (!selectedCourseId || !exclusionParticipantId) return;
    setError('');
    setSuccess('');
    if (!validateExclusionForAction(false)) {
      return;
    }

    setBusy(true);
    try {
      await saveSrhtParticipantExclusion(selectedCourseId, exclusionParticipantId, buildExclusionPayload());
      await loadCourseDetail(selectedCourseId);
      setSuccess('Ausschlussverfahren gespeichert.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Ausschlussverfahren konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleFinalizeExclusion = async () => {
    if (!selectedCourseId || !exclusionParticipantId) return;
    setError('');
    setSuccess('');
    if (!validateExclusionForAction(true)) {
      return;
    }

    setBusy(true);
    try {
      await saveSrhtParticipantExclusion(selectedCourseId, exclusionParticipantId, buildExclusionPayload());
      await finalizeSrhtParticipantExclusion(selectedCourseId, exclusionParticipantId);
      await loadCourseDetail(selectedCourseId);
      setSuccess('Teilnehmer wurde aus dem Lehrgang ausgeschlossen.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Teilnehmer konnte nicht aus dem Lehrgang ausgeschlossen werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlockExclusion = async () => {
    if (!selectedCourseId || !exclusionParticipantId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await unlockSrhtParticipantExclusion(selectedCourseId, exclusionParticipantId, { reason: exclusionUnlockReason });
      await loadCourseDetail(selectedCourseId);
      setExclusionUnlockDialogOpen(false);
      setExclusionUnlockReason('');
      setSuccess('Ausschlussverfahren entsperrt.');
    } catch (actionError) {
      setError(actionError.response?.data?.error || 'Ausschlussverfahren konnte nicht entsperrt werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleExportExclusionPdf = async () => {
    if (!selectedCourseId || !exclusionParticipantId) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const response = await exportSrhtParticipantExclusionPdf(selectedCourseId, exclusionParticipantId);
      downloadPdfResponse(response, 'Ausschlussverfahren.pdf');
      setSuccess('PDF des Ausschlussverfahrens wurde erstellt.');
    } catch (actionError) {
      setError(await extractPdfErrorMessage(actionError, 'PDF des Ausschlussverfahrens konnte nicht erstellt werden.'));
    } finally {
      setBusy(false);
    }
  };

  const canShowCandidateFeedback = permissions.canEditCandidateFeedback || selectedCandidateFeedbackEntry?.status === 'finalized';
  const canEditSelectedCourseMasterData = Boolean(permissions.canManageAdministration && selectedCourse && !isSelectedCourseLocked);
  const canCloseSelectedCourse = Boolean(permissions.canManageAdministration && selectedCourse && selectedCourse.status !== 'closed' && selectedCourse.status !== 'archived');
  const canDeleteSelectedCourse = Boolean(permissions.canManageAdministration && selectedCourse && ['draft', 'closed'].includes(selectedCourse.status));

  if (loading) {
    return (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Lehrgang SRHT
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verwaltung, Tagesfeedbacks und Wochenauswertungen für den Grundausbildungslehrgang.
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <CollapsibleModuleHeaderCard
          storageKey={SRHT_COURSE_HEADER_COLLAPSE_STORAGE_KEY}
          summary={(
            <FormControl fullWidth size="small" disabled={courses.length === 0}>
              <InputLabel id="srht-course-select-label">Lehrgang</InputLabel>
              <Select
                labelId="srht-course-select-label"
                label="Lehrgang"
                value={selectedCourseId}
                onChange={(event) => setSelectedCourseId(String(event.target.value))}
              >
                {courses.map((course) => (
                  <MenuItem key={course.id} value={String(course.id)}>{course.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        >
          {selectedCourse ? (
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Zeitraum: ${formatDate(selectedCourse.start_date)} - ${formatDate(selectedCourse.end_date)}`} />
                <Chip label={`Lehrgangstage: ${courseDetail?.days?.length || 0}`} color="primary" />
                <Chip label={`Teilnehmer: ${courseDetail?.participants?.length || 0}`} color="secondary" />
                <Chip label={getCourseStatusLabel(selectedCourse.status)} color={getCourseStatusChipColor(selectedCourse.status)} />
              </Stack>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                {permissions.canViewEvaluations ? (
                  <Stack spacing={0.75}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 320 } }}>
                        <InputLabel id="srht-pdf-section-label">PDF-Inhalt</InputLabel>
                        <Select
                          labelId="srht-pdf-section-label"
                          label="PDF-Inhalt"
                          value={selectedPdfExportType}
                          onChange={(event) => setSelectedPdfExportType(String(event.target.value))}
                        >
                          {COURSE_PDF_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedCoursePdfOption.requiresParticipantSelection ? (
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 320 } }}>
                          <InputLabel id="srht-pdf-participant-label">Teilnehmer</InputLabel>
                          <Select
                            labelId="srht-pdf-participant-label"
                            label="Teilnehmer"
                            multiple
                            value={selectedPdfParticipantIds}
                            onChange={(event) => setSelectedPdfParticipantIds(normalizeMultiSelectValue(event.target.value).map((value) => String(value)))}
                            renderValue={(selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return selected
                                .map((value) => coursePdfParticipantNameById.get(String(value)) || value)
                                .join(', ');
                            }}
                          >
                            {(courseDetail?.participants || []).map((participant) => {
                              const participantId = String(participant.id);
                              return (
                                <MenuItem key={participantId} value={participantId}>
                                  <Checkbox checked={selectedPdfParticipantIds.includes(participantId)} />
                                  {participant.full_name || '-'}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      ) : null}
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleExportPdf}
                        disabled={busy || !selectedCourseId}
                      >
                        PDF exportieren
                      </Button>
                    </Stack>
                    {selectedCoursePdfOption.singlePdfPerParticipant ? (
                      <Typography variant="caption" color="text.secondary">
                        Mehrere ausgewählte Teilnehmer werden als einzelne PDFs nacheinander heruntergeladen.
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}
                {permissions.canManageAdministration ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                    {selectedCourse.status === 'closed' ? (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<LockOpenIcon />}
                        onClick={() => setUnlockCourseDialogOpen(true)}
                        disabled={busy}
                      >
                        Wieder öffnen
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<VerifiedIcon />}
                        onClick={() => setCloseCourseDialogOpen(true)}
                        disabled={busy || !canCloseSelectedCourse}
                      >
                        Lehrgang abschließen
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => setDeleteCourseDialogOpen(true)}
                      disabled={busy || !canDeleteSelectedCourse}
                    >
                      Lehrgang löschen
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          ) : (
            <Alert severity="info">Noch kein Lehrgang ausgewählt.</Alert>
          )}
        </CollapsibleModuleHeaderCard>

        <Tabs value={resolvedActiveTab} onChange={(event, value) => setActiveTab(value)} variant="scrollable" allowScrollButtonsMobile>
          {visibleTabs.includes(SRHT_TAB_MANAGE) ? <Tab value={SRHT_TAB_MANAGE} label="Verwaltung" /> : null}
          {visibleTabs.includes(SRHT_TAB_CANDIDATE) ? <Tab value={SRHT_TAB_CANDIDATE} label="Tagesfeedback Teilnehmer" /> : null}
          {visibleTabs.includes(SRHT_TAB_INSTRUCTOR) ? <Tab value={SRHT_TAB_INSTRUCTOR} label="Tagesfeedback Lehrhöhenretter" /> : null}
          {visibleTabs.includes(SRHT_TAB_EVALUATION) ? <Tab value={SRHT_TAB_EVALUATION} label="Auswertung" /> : null}
        </Tabs>

        {resolvedActiveTab === SRHT_TAB_MANAGE ? (
          <Stack spacing={2}>
            {permissions.canManageAdministration ? (
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Neuen Lehrgang anlegen</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Lehrgangsbezeichnung"
                          value={createForm.label}
                          onChange={(event) => setCreateForm((currentValue) => ({ ...currentValue, label: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <DateInputField
                          fullWidth
                          size="small"
                          label="Startdatum"
                          value={createForm.startDate}
                          onChange={(value) => setCreateForm((currentValue) => ({ ...currentValue, startDate: value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <DateInputField
                          fullWidth
                          size="small"
                          label="Enddatum"
                          value={createForm.endDate}
                          onChange={(value) => setCreateForm((currentValue) => ({ ...currentValue, endDate: value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Gesamtstunden"
                          type="number"
                          value={createForm.totalHours}
                          onChange={(event) => setCreateForm((currentValue) => ({ ...currentValue, totalHours: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} lg={9}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Notizen"
                          value={createForm.notes}
                          onChange={(event) => setCreateForm((currentValue) => ({ ...currentValue, notes: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} lg={8}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="srht-assessment-events-label">Abgeschlossene Eignungsverfahren</InputLabel>
                          <Select
                            multiple
                            labelId="srht-assessment-events-label"
                            label="Abgeschlossene Eignungsverfahren"
                            value={createForm.assessmentEventIds}
                            onChange={(event) => {
                              const nextValues = normalizeMultiSelectValue(event.target.value).map((value) => Number(value));
                              setCreateForm((currentValue) => ({
                                ...currentValue,
                                assessmentEventIds: nextValues,
                                participantUserIds: currentValue.participantUserIds.filter((participantId) => (
                                  selectedAssessmentParticipantOptions.some((option) => Number(option.userId) === Number(participantId))
                                )),
                              }));
                            }}
                            renderValue={(selected) => (selected.length ? selected.map((value) => {
                              const eventRow = (referenceOptions.assessmentEvents || []).find((item) => Number(item.id) === Number(value));
                              return eventRow?.label || `Verfahren ${value}`;
                            }).join(', ') : 'Keine Verfahren gewählt')}
                          >
                            {(referenceOptions.assessmentEvents || []).map((event) => (
                              <MenuItem key={event.id} value={event.id}>{event.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="srht-participants-label">Teilnehmer</InputLabel>
                          <Select
                            multiple
                            labelId="srht-participants-label"
                            label="Teilnehmer"
                            value={createForm.participantUserIds}
                            onChange={(event) => setCreateForm((currentValue) => ({
                              ...currentValue,
                              participantUserIds: normalizeMultiSelectValue(event.target.value).map((value) => Number(value)),
                            }))}
                            renderValue={(selected) => (selected.length ? selected.map((value) => {
                              const participantOption = selectedAssessmentParticipantOptions.find((option) => Number(option.userId) === Number(value));
                              return participantOption?.fullName || `Benutzer ${value}`;
                            }).join(', ') : 'Keine Teilnehmer gewählt')}
                          >
                            {selectedAssessmentParticipantOptions.map((option) => (
                              <MenuItem key={option.userId} value={option.userId}>{option.fullName}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={4}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<PlaylistAddIcon />}
                          onClick={handleOpenTrainingDayDialog}
                          disabled={!createForm.startDate || !createForm.endDate}
                        >
                          Lehrgangstage auswählen
                        </Button>
                      </Grid>
                      <Grid item xs={12} lg={8}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {isTrainingDayRangeReady
                              ? `${createForm.trainingDays.length} Lehrgangstag${createForm.trainingDays.length === 1 ? '' : 'e'} innerhalb des Zeitraums ausgewählt.`
                              : 'Start- und Enddatum definieren, dann können die Lehrgangstage per Kalender ausgewählt werden.'}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {createForm.trainingDays.map((courseDay) => (
                              <Chip
                                key={courseDay}
                                label={formatDate(courseDay)}
                                onDelete={() => setCreateForm((currentValue) => ({
                                  ...currentValue,
                                  trainingDays: currentValue.trainingDays.filter((value) => value !== courseDay),
                                }))}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>
                    <Box>
                      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleCreateCourse} disabled={busy}>
                        Lehrgang anlegen
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ) : null}

            {selectedCourse ? (
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Stammdaten</Typography>
                    {selectedCourse.status === 'closed' ? (
                      <Alert severity="info">
                        Dieser Lehrgang ist abgeschlossen. Stammdaten und Inhalte sind schreibgeschützt, bis der Lehrgang mit Begründung wieder geöffnet wird.
                      </Alert>
                    ) : null}
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Lehrgangsbezeichnung"
                          value={courseAdminForm.label}
                          onChange={(event) => setCourseAdminForm((currentValue) => ({ ...currentValue, label: event.target.value }))}
                          disabled={!canEditSelectedCourseMasterData}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <DateInputField
                          fullWidth
                          size="small"
                          label="Startdatum"
                          value={courseAdminForm.startDate}
                          onChange={(value) => setCourseAdminForm((currentValue) => ({ ...currentValue, startDate: value }))}
                          disabled={!canEditSelectedCourseMasterData}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <DateInputField
                          fullWidth
                          size="small"
                          label="Enddatum"
                          value={courseAdminForm.endDate}
                          onChange={(value) => setCourseAdminForm((currentValue) => ({ ...currentValue, endDate: value }))}
                          disabled={!canEditSelectedCourseMasterData}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Gesamtstunden"
                          value={courseAdminForm.totalHours}
                          onChange={(event) => setCourseAdminForm((currentValue) => ({ ...currentValue, totalHours: event.target.value }))}
                          disabled={!canEditSelectedCourseMasterData}
                        />
                      </Grid>
                      <Grid item xs={12} lg={9}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Notizen"
                          value={courseAdminForm.notes}
                          onChange={(event) => setCourseAdminForm((currentValue) => ({ ...currentValue, notes: event.target.value }))}
                          disabled={!canEditSelectedCourseMasterData}
                        />
                      </Grid>
                    </Grid>
                    {permissions.canManageAdministration ? (
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleUpdateCourseMasterData}
                          disabled={busy || !canEditSelectedCourseMasterData}
                        >
                          Stammdaten speichern
                        </Button>
                      </Box>
                    ) : null}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Eignungsverfahren</Typography>
                          <Stack spacing={1}>
                            {(courseDetail?.assessmentSources || []).map((source) => (
                              <Typography key={source.id} variant="body2">
                                {source.assessment_label} ({formatDate(source.assessment_date)})
                              </Typography>
                            ))}
                          </Stack>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Lehrgangstage</Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {(courseDetail?.days || []).map((day) => (
                              <Chip key={day.id} label={`Tag ${day.training_day_index}: ${formatDate(day.course_date)}`} />
                            ))}
                          </Stack>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">Es ist noch kein Lehrgang vorhanden oder auswählbar.</Alert>
            )}
          </Stack>
        ) : null}

        {resolvedActiveTab === SRHT_TAB_CANDIDATE ? (
          selectedCourse ? (
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Gemeinsames Teilnehmerfeedback pro Lehrgangstag</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="candidate-day-label">Lehrgangstag</InputLabel>
                        <Select
                          labelId="candidate-day-label"
                          label="Lehrgangstag"
                          value={selectedCandidateDayId}
                          onChange={(event) => setSelectedCandidateDayId(String(event.target.value))}
                        >
                          {(courseDetail?.days || []).map((day) => (
                            <MenuItem key={day.id} value={String(day.id)}>{`Tag ${day.training_day_index} - ${formatDate(day.course_date)}`}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      {selectedCandidateFeedbackEntry ? (
                        <Chip label={selectedCandidateFeedbackEntry.status === 'finalized' ? 'Finalisiert' : 'Entwurf'} color={getStatusChipColor(selectedCandidateFeedbackEntry.status)} />
                      ) : (
                        <Chip label="Noch kein Feedback vorhanden" />
                      )}
                    </Grid>
                  </Grid>

                  {!canShowCandidateFeedback ? (
                    <Alert severity="info">Das Teilnehmerfeedback ist für Lehrhöhenretter erst nach der Finalisierung sichtbar.</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {isSelectedCourseLocked ? (
                        <Alert severity="info">Der Lehrgang ist abgeschlossen. Änderungen am Teilnehmerfeedback sind erst nach einer Wiederöffnung mit Begründung möglich.</Alert>
                      ) : null}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={8}
                            label="Positiv"
                            value={candidateFeedbackForm.positiveText}
                            onChange={(event) => setCandidateFeedbackForm((currentValue) => ({ ...currentValue, positiveText: event.target.value }))}
                            disabled={!permissions.canEditCandidateFeedback || selectedCandidateFeedbackEntry?.status === 'finalized' || isSelectedCourseLocked}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={8}
                            label="Negativ"
                            value={candidateFeedbackForm.negativeText}
                            onChange={(event) => setCandidateFeedbackForm((currentValue) => ({ ...currentValue, negativeText: event.target.value }))}
                            disabled={!permissions.canEditCandidateFeedback || selectedCandidateFeedbackEntry?.status === 'finalized' || isSelectedCourseLocked}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={8}
                            label="Offene Fragen"
                            value={candidateFeedbackForm.openQuestionsText}
                            onChange={(event) => setCandidateFeedbackForm((currentValue) => ({ ...currentValue, openQuestionsText: event.target.value }))}
                            disabled={!permissions.canEditCandidateFeedback || selectedCandidateFeedbackEntry?.status === 'finalized' || isSelectedCourseLocked}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  )}

                  {permissions.canEditCandidateFeedback ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleCandidateFeedbackSave} disabled={busy || !selectedCandidateDayId || selectedCandidateFeedbackEntry?.status === 'finalized' || isSelectedCourseLocked}>
                        Speichern
                      </Button>
                      <Button variant="outlined" startIcon={<VerifiedIcon />} onClick={handleCandidateFeedbackFinalize} disabled={busy || !selectedCandidateDayId || isSelectedCourseLocked}>
                        Finalisieren
                      </Button>
                      {selectedCandidateFeedbackEntry?.status === 'finalized' ? (
                        <Button variant="outlined" color="warning" startIcon={<LockOpenIcon />} onClick={handleCandidateFeedbackUnlock} disabled={busy || isSelectedCourseLocked}>
                          Entsperren
                        </Button>
                      ) : null}
                    </Stack>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info">Bitte zunächst einen Lehrgang auswählen.</Alert>
          )
        ) : null}

        {resolvedActiveTab === SRHT_TAB_INSTRUCTOR ? (
          selectedCourse ? (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Lehrhöhenretter-Tagesfeedback</Typography>
                    {isSelectedCourseLocked ? (
                      <Alert severity="info">Der Lehrgang ist abgeschlossen. Änderungen sind erst nach einer Wiederöffnung mit Begründung möglich.</Alert>
                    ) : null}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                        gap: 1,
                      }}
                    >
                      {instructorDayTiles.map((tile) => {
                        const isSelected = tile.dayId && String(tile.dayId) === String(selectedInstructorDayId);
                        const statusLabel = tile.status === 'finalized' ? 'Finalisiert' : tile.status === 'open' ? 'Offen' : 'Kein Tag';
                        const statusColor = tile.status === 'finalized' ? 'success.main' : tile.status === 'open' ? 'warning.main' : 'text.disabled';

                        return (
                          <ButtonBase
                            key={`instructor-day-tile-${tile.trainingDayIndex}`}
                            onClick={() => tile.dayId && setSelectedInstructorDayId(String(tile.dayId))}
                            disabled={!tile.dayId}
                            sx={{ textAlign: 'left', borderRadius: 2 }}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                width: '100%',
                                minHeight: 76,
                                p: 1,
                                borderRadius: 2,
                                borderColor: isSelected ? 'primary.main' : tile.status === 'finalized' ? 'success.light' : 'divider',
                                bgcolor: !tile.dayId ? 'action.hover' : isSelected ? 'primary.50' : 'background.paper',
                              }}
                            >
                              <Stack spacing={0.35}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  {`Tag ${tile.trainingDayIndex}`}
                                </Typography>
                                <Typography variant="caption" sx={{ color: statusColor, fontWeight: 600 }}>
                                  {statusLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {tile.dateLabel || 'nicht angelegt'}
                                </Typography>
                              </Stack>
                            </Paper>
                          </ButtonBase>
                        );
                      })}
                    </Box>

                    <Stack spacing={0.75}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                        Durchführende Lehrhöhenretter
                      </Typography>
                      <FormControl
                        fullWidth
                        size="small"
                        error={instructorWorkstepValidationVisible && instructorWorkstepValidation.missingInstructorSelection}
                      >
                        <Select
                          multiple
                          displayEmpty
                          value={instructorFeedbackForm.instructorUserIds}
                          onChange={(event) => setInstructorFeedbackForm((currentValue) => ({
                            ...currentValue,
                            instructorUserIds: normalizeMultiSelectValue(event.target.value).map((value) => Number(value)),
                          }))}
                          renderValue={(selected) => {
                            if (!selected.length) {
                              return 'Auswählen';
                            }
                            return selected.map((value) => {
                              const option = (referenceOptions.dayInstructorOptions || []).find((item) => Number(item.id) === Number(value));
                              return option?.fullName || `Benutzer ${value}`;
                            }).join(', ');
                          }}
                          disabled={isInstructorWorkstepReadOnly}
                          inputProps={{ 'aria-label': 'Durchführende Lehrhöhenretter' }}
                        >
                          {(referenceOptions.dayInstructorOptions || []).map((option) => (
                            <MenuItem key={option.id} value={option.id}>{option.fullName}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {instructorWorkstepValidationVisible && instructorWorkstepValidation.missingInstructorSelection
                            ? 'Bitte mindestens einen durchführenden Lehrhöhenretter auswählen.'
                            : ' '}
                        </FormHelperText>
                      </FormControl>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={8}
                          placeholder="Nicht (vollständig) geschaffte Unterrichte / Übungen"
                          value={instructorFeedbackForm.incompleteLessonsText}
                          onChange={(event) => setInstructorFeedbackForm((currentValue) => ({ ...currentValue, incompleteLessonsText: event.target.value }))}
                          disabled={isInstructorWorkstepReadOnly}
                          inputProps={{ 'aria-label': 'Nicht (vollständig) geschaffte Unterrichte / Übungen' }}
                          sx={MULTILINE_PLACEHOLDER_SX}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={8}
                          placeholder="Organisatorische / Technische Probleme in der Planung / Durchführung"
                          value={instructorFeedbackForm.planningIssuesText}
                          onChange={(event) => setInstructorFeedbackForm((currentValue) => ({ ...currentValue, planningIssuesText: event.target.value }))}
                          disabled={isInstructorWorkstepReadOnly}
                          inputProps={{ 'aria-label': 'Organisatorische / Technische Probleme in der Planung / Durchführung' }}
                          sx={MULTILINE_PLACEHOLDER_SX}
                        />
                      </Grid>
                    </Grid>

                    <InstructorSafetyIncidentSection
                      safetyIncidentAnswer={instructorFeedbackForm.safetyIncidentAnswer}
                      onSafetyIncidentAnswerChange={(value) => setInstructorFeedbackForm((currentValue) => ({
                        ...currentValue,
                        safetyIncidentAnswer: value,
                      }))}
                      isReadOnly={isInstructorWorkstepReadOnly}
                      incidentFormState={incidentFormState}
                      onIncidentFieldChange={handleInstructorIncidentFieldChange}
                      incidentOptions={incidentOptions}
                      incidentAttachmentFiles={incidentAttachmentFiles}
                      onIncidentAttachmentFilesChange={setIncidentAttachmentFiles}
                      selectedSafetyIncident={selectedSafetyIncident}
                      onDeleteLinkedIncidentAsset={handleDeleteLinkedIncidentAsset}
                      deletingIncidentAssetId={deletingIncidentAssetId}
                      busy={busy}
                    />
                  </Stack>
                </CardContent>
              </Card>

              {selectedInstructorDayRequiresReviews ? (
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Bewertung der Lehrgangsteilnehmer für den ausgewählten Tag</Typography>
                      {relevantInstructorParticipants.length === 0 ? (
                        <Alert severity="info">Für den ausgewählten Lehrgangstag liegen keine bewertungspflichtigen Teilnehmer vor.</Alert>
                      ) : null}
                      <Stack spacing={1.5}>
                        {relevantInstructorParticipants.map((participant) => {
                          const draft = dayReviewDrafts[participant.id] || {};
                          const isWithdrawn = participant.status === 'withdrawn';
                          const missingFields = instructorWorkstepValidation.missingScoreFieldsByParticipantId[participant.id] || [];
                          return (
                            <Paper key={participant.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, opacity: isWithdrawn ? 0.8 : 1 }}>
                              <Stack spacing={1.5}>
                                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                  <Typography sx={{ fontWeight: 700, textDecoration: isWithdrawn ? 'line-through' : 'none' }}>{participant.full_name}</Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {isWithdrawn ? (
                                      <Chip color="error" label={getParticipantWithdrawalLabel(participant)} />
                                    ) : null}
                                    {isParticipantExamBlocked(participant) ? (
                                      <Chip color="warning" label={getParticipantExamBlockLabel(participant)} />
                                    ) : null}
                                  </Stack>
                                </Stack>

                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                      xs: '1fr',
                                      sm: 'repeat(2, minmax(0, 1fr))',
                                      lg: 'repeat(3, minmax(0, 1fr))',
                                    },
                                    gap: 1.5,
                                    alignItems: 'start',
                                  }}
                                >
                                  {INSTRUCTOR_REVIEW_FIELDS.map(({ key, label }) => (
                                    <Box key={`${participant.id}-${key}`}>
                                      <Stack spacing={0.75}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25, minHeight: 32 }}>
                                          {label}
                                        </Typography>
                                        <FormControl fullWidth size="small" error={instructorWorkstepValidationVisible && missingFields.includes(key)}>
                                          <Select
                                            displayEmpty
                                            value={draft[key] === '' || typeof draft[key] === 'undefined' || draft[key] === null ? '' : Number(draft[key])}
                                            onChange={(event) => handleDayReviewChange(participant.id, { [key]: event.target.value })}
                                            disabled={isInstructorWorkstepReadOnly}
                                            renderValue={(selectedValue) => {
                                              if (selectedValue === '' || typeof selectedValue === 'undefined' || selectedValue === null) {
                                                return 'Auswählen';
                                              }
                                              const selectedOption = SCORE_OPTIONS.find((option) => Number(option.value) === Number(selectedValue));
                                              return selectedOption?.label || String(selectedValue);
                                            }}
                                            inputProps={{ 'aria-label': label }}
                                          >
                                            <MenuItem value=""><em>Auswählen</em></MenuItem>
                                            {SCORE_OPTIONS.map((option) => (
                                              <MenuItem key={`${key}-${option.value}`} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                          </Select>
                                          <FormHelperText>
                                            {instructorWorkstepValidationVisible && missingFields.includes(key) ? 'Pflichtfeld.' : ' '}
                                          </FormHelperText>
                                        </FormControl>
                                      </Stack>
                                    </Box>
                                  ))}
                                  <Box>
                                    <Stack spacing={0.75}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25, minHeight: 32 }}>
                                        Fehlzeit (h)
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={draft.absenceHours ?? 0}
                                        onChange={(event) => handleDayReviewChange(participant.id, { absenceHours: event.target.value })}
                                        disabled={isInstructorWorkstepReadOnly}
                                        inputProps={{ 'aria-label': 'Fehlzeit (h)' }}
                                        helperText=" "
                                      />
                                    </Stack>
                                  </Box>
                                </Box>
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={2}
                                  label="Bemerkungen"
                                  value={draft.remarkText || ''}
                                  onChange={(event) => handleDayReviewChange(participant.id, { remarkText: event.target.value })}
                                  disabled={isInstructorWorkstepReadOnly}
                                />
                                {instructorWorkstepValidationVisible && missingFields.length > 0 ? (
                                  <Alert severity="warning">
                                    Pflichtfelder fehlen: {INSTRUCTOR_REVIEW_FIELDS.filter((field) => missingFields.includes(field.key)).map((field) => field.label).join(', ')}.
                                  </Alert>
                                ) : null}
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}

              {permissions.canEditInstructorFeedback ? (
                <Stack spacing={1.5}>
                  {isInstructorWorkstepFinalized ? (
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack spacing={1.25}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bearbeitungsschutz</Typography>
                          <Chip label="Finalisiert" color="success" size="small" />
                        </Stack>
                        <TextField
                          label="Begründung zum Entsperren"
                          value={instructorUnlockReason}
                          onChange={(event) => setInstructorUnlockReason(event.target.value)}
                          placeholder="z. B. Zahlendreher oder fehlende Ergänzung"
                          required
                          error={!String(instructorUnlockReason || '').trim()}
                          helperText={!String(instructorUnlockReason || '').trim() ? 'Bitte eine Begründung als Freitext eingeben.' : ' '}
                        />
                        <Button variant="outlined" color="warning" startIcon={<LockOpenIcon />} onClick={handleInstructorFeedbackUnlock} disabled={busy || !String(instructorUnlockReason || '').trim() || isSelectedCourseLocked}>
                          Bogen entsperren
                        </Button>
                      </Stack>
                    </Paper>
                  ) : null}

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.25}
                    sx={{ position: { xs: 'sticky', md: 'static' }, bottom: { xs: 8, md: 'auto' }, zIndex: 2, bgcolor: { xs: 'background.paper', md: 'transparent' }, p: { xs: 1, md: 0 }, borderRadius: { xs: 2.5, md: 0 }, boxShadow: { xs: 3, md: 'none' } }}
                  >
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleInstructorFeedbackSave} disabled={busy || !selectedInstructorDayId || isInstructorWorkstepReadOnly} fullWidth>
                      Speichern
                    </Button>
                    <Button variant="contained" color="success" startIcon={<VerifiedIcon />} onClick={handleInstructorFeedbackFinalize} disabled={busy || !selectedInstructorDayId || isInstructorWorkstepReadOnly} fullWidth>
                      Finalisieren
                    </Button>
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Alert severity="info">Bitte zunächst einen Lehrgang auswählen.</Alert>
          )
        ) : null}

        {resolvedActiveTab === SRHT_TAB_EVALUATION ? (
          selectedCourse ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant={isOverallEvaluationSelected ? 'contained' : 'outlined'}
                  onClick={() => setIsOverallEvaluationSelected(true)}
                >
                  Gesamtleistung
                </Button>
                {[1, 2, 3].map((weekNumber) => (
                  <Button
                    key={`week-${weekNumber}`}
                    variant={!isOverallEvaluationSelected && selectedWeekNumber === weekNumber ? 'contained' : 'outlined'}
                    onClick={() => {
                      setIsOverallEvaluationSelected(false);
                      setSelectedWeekNumber(weekNumber);
                    }}
                  >
                    {`Teilnehmerleistung Woche ${weekNumber}`}
                  </Button>
                ))}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Stack
                    spacing={1.5}
                    sx={{ minHeight: 120 }}
                    onClick={(event) => {
                      if (event.target === event.currentTarget) {
                        setSelectedParticipantId('');
                      }
                    }}
                  >
                    {evaluationParticipantEntries.map((participantEntry) => {
                      const summary = participantEntry.summary || null;
                      const isSelected = String(participantEntry.participantId) === String(selectedParticipantId);
                      const overallScalePercent = getSrhtCategoryScalePercent(summary?.overallAverage);
                      const overallScaleTone = getSrhtCategoryScaleTone(summary?.overallAverage);

                      if (!isOverallEvaluationSelected) {
                        const feedbackStatusLabel = getSrhtFeedbackStatusLabel(summary?.storedEvaluation?.status);
                        return (
                          <Paper
                            key={`week-${selectedWeekNumber}-participant-${participantEntry.participantId}`}
                            variant="outlined"
                            sx={{
                              p: 1.25,
                              borderRadius: 2,
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              cursor: 'pointer',
                            }}
                            onClick={() => setSelectedParticipantId(isSelected ? '' : String(participantEntry.participantId))}
                          >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                              <Typography sx={{ fontWeight: 700, textDecoration: participantEntry.status === 'withdrawn' ? 'line-through' : 'none' }}>
                                {`Feedback: ${participantEntry.fullName}`}
                              </Typography>
                              <Chip label={feedbackStatusLabel} color={getStatusChipColor(summary?.storedEvaluation?.status || 'draft')} size="small" />
                            </Stack>
                          </Paper>
                        );
                      }

                      return (
                        <Paper
                          key={`week-${selectedWeekNumber}-participant-${participantEntry.participantId}`}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            cursor: 'pointer',
                          }}
                          onClick={() => setSelectedParticipantId(isSelected ? '' : String(participantEntry.participantId))}
                        >
                          <Stack spacing={1}>
                            <Typography sx={{ fontWeight: 700, textDecoration: participantEntry.status === 'withdrawn' ? 'line-through' : 'none' }}>
                              {participantEntry.fullName}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Chip label={`Ø ${formatAverage(summary?.overallAverage)}`} />
                              {summary?.strongestCategory ? <Chip label={`Stärkste: ${summary.strongestCategory.label}`} color="success" /> : null}
                              {summary?.weakestCategory ? <Chip label={`Schwächste: ${summary.weakestCategory.label}`} color="warning" /> : null}
                              {participantEntry.status === 'withdrawn' ? <Chip color="error" label={getParticipantWithdrawalLabel(participantEntry)} /> : null}
                              {isParticipantExamBlocked(participantEntry) ? <Chip color="warning" label={getParticipantExamBlockLabel(participantEntry)} /> : null}
                            </Stack>
                            <Stack spacing={0.75}>
                              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <Box />
                                <Chip
                                  size="small"
                                  label={overallScaleTone.label}
                                  sx={{
                                    height: 22,
                                    fontWeight: 700,
                                    backgroundColor: overallScaleTone.backgroundColor,
                                    color: overallScaleTone.textColor,
                                  }}
                                />
                              </Stack>
                              <Box sx={{ position: 'relative', pt: 1 }}>
                                <Box
                                  sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    background: summary?.overallAverage == null
                                      ? 'rgba(0, 0, 0, 0.08)'
                                      : 'linear-gradient(90deg, #C62828 0%, #F9A825 50%, #2E7D32 100%)',
                                  }}
                                />
                                {overallScalePercent !== null ? (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: `calc(${overallScalePercent}% - 7px)`,
                                      width: 14,
                                      height: 14,
                                      borderRadius: '50%',
                                      backgroundColor: '#FFFFFF',
                                      border: '3px solid #1B3A5C',
                                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                                    }}
                                  />
                                ) : null}
                              </Box>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">0</Typography>
                                <Typography variant="caption" color="text.secondary">1</Typography>
                                <Typography variant="caption" color="text.secondary">2</Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={7}>
                  {isOverallEvaluationSelected && selectedParticipant && selectedOverallSummary ? (
                    <Card>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                          >
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                              <Typography variant="h6" sx={{ fontWeight: 700, textDecoration: selectedParticipant.status === 'withdrawn' ? 'line-through' : 'none' }}>
                                {selectedParticipant.full_name}
                              </Typography>
                              {selectedParticipant.status === 'withdrawn' ? (
                                <Chip
                                  color="error"
                                  label={getParticipantWithdrawalLabel(selectedParticipant)}
                                />
                              ) : null}
                              {isParticipantExamBlocked(selectedParticipant) ? (
                                <Chip color="warning" label={getParticipantExamBlockLabel(selectedParticipant)} />
                              ) : null}
                            </Stack>
                            {canManageExclusions ? (
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => openExclusionDialogForParticipant(selectedParticipant)}
                                disabled={busy || !selectedParticipant || (isSelectedCourseLocked && !selectedParticipant.exclusionRecord)}
                              >
                                Ausschluss aus dem Lehrgang
                              </Button>
                            ) : null}
                          </Stack>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              borderColor: 'divider',
                              background: 'linear-gradient(180deg, rgba(27, 58, 92, 0.04) 0%, rgba(27, 58, 92, 0.01) 100%)',
                            }}
                          >
                            <Stack spacing={1.5}>
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                              >
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                  Gesamtleistung des Teilnehmers
                                </Typography>
                                <Chip
                                  size="small"
                                  label={getSrhtCategoryScaleTone(selectedOverallSummary.overallAverage).label}
                                  sx={{
                                    height: 24,
                                    fontWeight: 700,
                                    backgroundColor: getSrhtCategoryScaleTone(selectedOverallSummary.overallAverage).backgroundColor,
                                    color: getSrhtCategoryScaleTone(selectedOverallSummary.overallAverage).textColor,
                                  }}
                                />
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip label={`Ø ${formatAverage(selectedOverallSummary.overallAverage)} Punkte`} />
                                {selectedOverallSummary.strongestCategory ? <Chip label={`Stärkste Disziplin: ${selectedOverallSummary.strongestCategory.label}`} color="success" /> : null}
                                {selectedOverallSummary.weakestCategory ? <Chip label={`Schwächste Disziplin: ${selectedOverallSummary.weakestCategory.label}`} color="warning" /> : null}
                              </Stack>
                              <Box>
                                <Box sx={{ position: 'relative', pt: 1.25 }}>
                                  <Box
                                    sx={{
                                      height: 12,
                                      borderRadius: 999,
                                      background: selectedOverallSummary.overallAverage == null
                                        ? 'rgba(0, 0, 0, 0.08)'
                                        : 'linear-gradient(90deg, #C62828 0%, #F9A825 50%, #2E7D32 100%)',
                                    }}
                                  />
                                  {getSrhtCategoryScalePercent(selectedOverallSummary.overallAverage) !== null ? (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: `calc(${getSrhtCategoryScalePercent(selectedOverallSummary.overallAverage)}% - 9px)`,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        backgroundColor: '#FFFFFF',
                                        border: '4px solid #1B3A5C',
                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                                      }}
                                    />
                                  ) : null}
                                </Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                                  <Typography variant="caption" color="text.secondary">0</Typography>
                                  <Typography variant="caption" color="text.secondary">1</Typography>
                                  <Typography variant="caption" color="text.secondary">2</Typography>
                                </Stack>
                              </Box>
                            </Stack>
                          </Paper>

                          <Grid container spacing={1.5}>
                            {(selectedOverallSummary.categories || []).map((category) => {
                              const categoryScalePercent = getSrhtCategoryScalePercent(category.average);
                              const categoryTone = getSrhtCategoryScaleTone(category.average);

                              return (
                                <Grid item xs={12} sm={6} key={`${selectedParticipant.id}-overall-${category.key}`}>
                                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
                                    <Stack spacing={1.1}>
                                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                                        <Typography variant="body2" color="text.secondary">{category.label}</Typography>
                                        <Chip
                                          size="small"
                                          label={categoryTone.label}
                                          sx={{
                                            fontWeight: 700,
                                            backgroundColor: categoryTone.backgroundColor,
                                            color: categoryTone.textColor,
                                          }}
                                        />
                                      </Stack>
                                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatAverage(category.average)}</Typography>
                                      <Box>
                                        <Box sx={{ position: 'relative', pt: 1.5 }}>
                                          <Box
                                            sx={{
                                              height: 10,
                                              borderRadius: 999,
                                              background: category.average === null
                                                ? 'rgba(0, 0, 0, 0.08)'
                                                : 'linear-gradient(90deg, #C62828 0%, #F9A825 50%, #2E7D32 100%)',
                                            }}
                                          />
                                          {categoryScalePercent !== null ? (
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: `calc(${categoryScalePercent}% - 8px)`,
                                                width: 16,
                                                height: 16,
                                                borderRadius: '50%',
                                                backgroundColor: '#FFFFFF',
                                                border: '3px solid #1B3A5C',
                                                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                                              }}
                                            />
                                          ) : null}
                                        </Box>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                                          <Typography variant="caption" color="text.secondary">0</Typography>
                                          <Typography variant="caption" color="text.secondary">1</Typography>
                                          <Typography variant="caption" color="text.secondary">2</Typography>
                                        </Stack>
                                      </Box>
                                    </Stack>
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : null}

                  {!isOverallEvaluationSelected && selectedParticipant && selectedWeeklySummary ? (
                    <Card>
                      <CardContent>
                        <Stack spacing={2}>
                          {isSelectedCourseLocked ? (
                            <Alert severity="info">Der Lehrgang ist abgeschlossen. Änderungen an Wochenauswertungen sind erst nach einer Wiederöffnung mit Begründung möglich.</Alert>
                          ) : null}
                          <Typography variant="h6" sx={{ fontWeight: 700, textDecoration: selectedParticipant.status === 'withdrawn' ? 'line-through' : 'none' }}>
                            {selectedParticipant.full_name}
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              borderColor: 'divider',
                              background: 'linear-gradient(180deg, rgba(27, 58, 92, 0.04) 0%, rgba(27, 58, 92, 0.01) 100%)',
                            }}
                          >
                            <Stack spacing={1.5}>
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                              >
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                  {`Wochenleistung Woche ${selectedWeekNumber} bis Tag ${selectedWeeklySummary.dayLimit}`}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={getSrhtCategoryScaleTone(selectedWeeklySummary.overallAverage).label}
                                  sx={{
                                    height: 24,
                                    fontWeight: 700,
                                    backgroundColor: getSrhtCategoryScaleTone(selectedWeeklySummary.overallAverage).backgroundColor,
                                    color: getSrhtCategoryScaleTone(selectedWeeklySummary.overallAverage).textColor,
                                  }}
                                />
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip label={`Ø ${formatAverage(selectedWeeklySummary.overallAverage)} Punkte`} />
                                {selectedWeeklySummary.strongestCategory ? <Chip label={`Stärkste Disziplin: ${selectedWeeklySummary.strongestCategory.label}`} color="success" /> : null}
                                {selectedWeeklySummary.weakestCategory ? <Chip label={`Schwächste Disziplin: ${selectedWeeklySummary.weakestCategory.label}`} color="warning" /> : null}
                              </Stack>
                              <Box>
                                <Box sx={{ position: 'relative', pt: 1.25 }}>
                                  <Box
                                    sx={{
                                      height: 12,
                                      borderRadius: 999,
                                      background: selectedWeeklySummary.overallAverage == null
                                        ? 'rgba(0, 0, 0, 0.08)'
                                        : 'linear-gradient(90deg, #C62828 0%, #F9A825 50%, #2E7D32 100%)',
                                    }}
                                  />
                                  {getSrhtCategoryScalePercent(selectedWeeklySummary.overallAverage) !== null ? (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: `calc(${getSrhtCategoryScalePercent(selectedWeeklySummary.overallAverage)}% - 9px)`,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        backgroundColor: '#FFFFFF',
                                        border: '4px solid #1B3A5C',
                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                                      }}
                                    />
                                  ) : null}
                                </Box>
                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                                  <Typography variant="caption" color="text.secondary">0</Typography>
                                  <Typography variant="caption" color="text.secondary">1</Typography>
                                  <Typography variant="caption" color="text.secondary">2</Typography>
                                </Stack>
                              </Box>
                            </Stack>
                          </Paper>
                          <Grid container spacing={1.5}>
                            {(selectedWeeklySummary.categories || []).map((category) => {
                              const categoryScalePercent = getSrhtCategoryScalePercent(category.average);
                              const categoryTone = getSrhtCategoryScaleTone(category.average);

                              return (
                                <Grid item xs={12} sm={6} key={`${selectedParticipant.id}-${selectedWeekNumber}-${category.key}`}>
                                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
                                    <Stack spacing={1.1}>
                                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                                        <Typography variant="body2" color="text.secondary">{category.label}</Typography>
                                        <Chip
                                          size="small"
                                          label={categoryTone.label}
                                          sx={{
                                            fontWeight: 700,
                                            backgroundColor: categoryTone.backgroundColor,
                                            color: categoryTone.textColor,
                                          }}
                                        />
                                      </Stack>
                                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatAverage(category.average)}</Typography>
                                      <Box>
                                        <Box sx={{ position: 'relative', pt: 1.5 }}>
                                          <Box
                                            sx={{
                                              height: 10,
                                              borderRadius: 999,
                                              background: category.average === null
                                                ? 'rgba(0, 0, 0, 0.08)'
                                                : 'linear-gradient(90deg, #C62828 0%, #F9A825 50%, #2E7D32 100%)',
                                            }}
                                          />
                                          {categoryScalePercent !== null ? (
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: `calc(${categoryScalePercent}% - 8px)`,
                                                width: 16,
                                                height: 16,
                                                borderRadius: '50%',
                                                backgroundColor: '#FFFFFF',
                                                border: '3px solid #1B3A5C',
                                                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                                              }}
                                            />
                                          ) : null}
                                        </Box>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                                          <Typography variant="caption" color="text.secondary">0</Typography>
                                          <Typography variant="caption" color="text.secondary">1</Typography>
                                          <Typography variant="caption" color="text.secondary">2</Typography>
                                        </Stack>
                                      </Box>
                                    </Stack>
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>

                          <Divider />

                          <TextField
                            select
                            fullWidth
                            label="Einschätzung der Lehrgangsleitung"
                            value={evaluationForm.decisionCode}
                            onChange={(event) => setEvaluationForm((currentValue) => ({ ...currentValue, decisionCode: event.target.value }))}
                            disabled={isWeeklyEvaluationReadOnly}
                            error={evaluationValidationVisible && weeklyEvaluationValidation.missingDecisionCode}
                            helperText={evaluationValidationVisible && weeklyEvaluationValidation.missingDecisionCode ? 'Bitte eine Einschätzung auswählen.' : ' '}
                          >
                            <MenuItem value=""><em>Keine Auswahl</em></MenuItem>
                            {DECISION_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Feedback"
                            value={evaluationForm.feedbackText}
                            onChange={(event) => setEvaluationForm((currentValue) => ({ ...currentValue, feedbackText: event.target.value }))}
                            disabled={isWeeklyEvaluationReadOnly}
                            error={evaluationValidationVisible && weeklyEvaluationValidation.missingFeedbackText}
                            helperText={evaluationValidationVisible && weeklyEvaluationValidation.missingFeedbackText ? 'Bitte das Feedback ausfüllen.' : ' '}
                          />

                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Bemerkungen"
                            value={evaluationForm.noteText}
                            onChange={(event) => setEvaluationForm((currentValue) => ({ ...currentValue, noteText: event.target.value }))}
                            disabled={isWeeklyEvaluationReadOnly}
                          />

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={4}>
                              <Stack spacing={0.75}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                                  Feedback an Tag
                                </Typography>
                                <FormControl fullWidth size="small" error={evaluationValidationVisible && weeklyEvaluationValidation.missingFeedbackCourseDayId}>
                                  <Select
                                    displayEmpty
                                    value={evaluationForm.feedbackCourseDayId}
                                    onChange={(event) => setEvaluationForm((currentValue) => ({ ...currentValue, feedbackCourseDayId: String(event.target.value) }))}
                                    disabled={isWeeklyEvaluationReadOnly}
                                    inputProps={{ 'aria-label': 'Feedback an Tag' }}
                                    renderValue={(selectedValue) => {
                                      if (!selectedValue) {
                                        return 'Auswählen';
                                      }
                                      const selectedDay = (courseDetail?.days || []).find((day) => String(day.id) === String(selectedValue));
                                      return selectedDay ? `Tag ${selectedDay.training_day_index}` : String(selectedValue);
                                    }}
                                  >
                                    <MenuItem value=""><em>Auswählen</em></MenuItem>
                                    {(courseDetail?.days || []).map((day) => (
                                      <MenuItem key={`evaluation-day-${day.id}`} value={String(day.id)}>{`Tag ${day.training_day_index}`}</MenuItem>
                                    ))}
                                  </Select>
                                  <FormHelperText>
                                    {evaluationValidationVisible && weeklyEvaluationValidation.missingFeedbackCourseDayId ? 'Bitte einen Lehrgangstag auswählen.' : ' '}
                                  </FormHelperText>
                                </FormControl>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <Stack spacing={0.75}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                                  Feedback gegeben durch
                                </Typography>
                                <FormControl fullWidth size="small" error={evaluationValidationVisible && weeklyEvaluationValidation.missingFeedbackGiverUserIds}>
                                  <Select
                                    multiple
                                    displayEmpty
                                    value={evaluationForm.feedbackGiverUserIds}
                                    onChange={(event) => setEvaluationForm((currentValue) => ({
                                      ...currentValue,
                                      feedbackGiverUserIds: normalizeMultiSelectValue(event.target.value).map((value) => Number(value)),
                                    }))}
                                    renderValue={(selected) => {
                                      if (!selected.length) {
                                        return 'Auswählen';
                                      }
                                      return selected.map((value) => {
                                        const option = (referenceOptions.weeklyFeedbackGiverOptions || []).find((item) => Number(item.id) === Number(value));
                                        return option?.fullName || `Benutzer ${value}`;
                                      }).join(', ');
                                    }}
                                    disabled={isWeeklyEvaluationReadOnly}
                                    inputProps={{ 'aria-label': 'Feedback gegeben durch' }}
                                  >
                                    {(referenceOptions.weeklyFeedbackGiverOptions || []).map((option) => (
                                      <MenuItem key={`weekly-giver-${option.id}`} value={option.id}>{option.fullName}</MenuItem>
                                    ))}
                                  </Select>
                                  <FormHelperText>
                                    {evaluationValidationVisible && weeklyEvaluationValidation.missingFeedbackGiverUserIds ? 'Bitte mindestens eine Person auswählen.' : ' '}
                                  </FormHelperText>
                                </FormControl>
                              </Stack>
                            </Grid>
                          </Grid>

                          {permissions.canEditEvaluations ? (
                            <Stack spacing={1.5}>
                              {isWeeklyEvaluationFinalized ? (
                                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                  <Stack spacing={1.25}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bearbeitungsschutz</Typography>
                                      <Chip label="Finalisiert" color="success" size="small" />
                                    </Stack>
                                    <TextField
                                      label="Begründung zum Entsperren"
                                      value={evaluationUnlockReason}
                                      onChange={(event) => setEvaluationUnlockReason(event.target.value)}
                                      placeholder="z. B. Zahlendreher oder fehlende Ergänzung"
                                      required
                                      error={!String(evaluationUnlockReason || '').trim()}
                                      helperText={!String(evaluationUnlockReason || '').trim() ? 'Bitte eine Begründung als Freitext eingeben.' : ' '}
                                    />
                                    <Button variant="outlined" color="warning" startIcon={<LockOpenIcon />} onClick={handleUnlockWeeklyEvaluation} disabled={busy || !String(evaluationUnlockReason || '').trim() || isSelectedCourseLocked}>
                                      Bogen entsperren
                                    </Button>
                                  </Stack>
                                </Paper>
                              ) : null}

                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.25}
                                sx={{ position: { xs: 'sticky', md: 'static' }, bottom: { xs: 8, md: 'auto' }, zIndex: 2, bgcolor: { xs: 'background.paper', md: 'transparent' }, p: { xs: 1, md: 0 }, borderRadius: { xs: 2.5, md: 0 }, boxShadow: { xs: 3, md: 'none' } }}
                              >
                                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveWeeklyEvaluation} disabled={busy || isWeeklyEvaluationReadOnly} fullWidth>
                                  Speichern
                                </Button>
                                <Button variant="contained" color="success" startIcon={<VerifiedIcon />} onClick={handleFinalizeWeeklyEvaluation} disabled={busy || isWeeklyEvaluationReadOnly} fullWidth>
                                  Finalisieren
                                </Button>
                              </Stack>
                            </Stack>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : null}
                </Grid>
              </Grid>
            </Stack>
          ) : (
            <Alert severity="info">Bitte zunächst einen Lehrgang auswählen.</Alert>
          )
        ) : null}

        <Dialog open={exclusionConfirmDialogOpen} onClose={() => setExclusionConfirmDialogOpen(false)}>
          <DialogTitle>Ausschlussverfahren beginnen</DialogTitle>
          <DialogContent>
            <Typography>
              {exclusionParticipant ? `Soll das Ausschlussverfahren für den Teilnehmer ${exclusionParticipant.full_name} wirklich begonnen werden?` : ''}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExclusionConfirmDialogOpen(false)}>Nein</Button>
            <Button variant="contained" color="error" onClick={handleConfirmExclusionStart}>
              Ja
            </Button>
          </DialogActions>
        </Dialog>

        <SrhtAbsenceDecisionDialog
          open={absenceDecisionDialogOpen}
          busy={busy}
          participants={absenceDecisionParticipants}
          selections={absenceDecisionSelections}
          onSelectionChange={(participantId, value) => setAbsenceDecisionSelections((currentValue) => ({
            ...currentValue,
            [String(participantId)]: value,
          }))}
          onClose={resetAbsenceDecisionDialog}
          onConfirm={handleConfirmAbsenceDecisions}
        />

        <Dialog open={exclusionDialogOpen} onClose={handleCloseExclusionDialog} fullWidth maxWidth="lg">
          <DialogTitle>
            {exclusionParticipant ? `Ausschlussverfahren ${exclusionParticipant.full_name}` : 'Ausschlussverfahren'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {isExclusionFinalized ? (
                <Alert
                  severity="info"
                  action={canManageExclusions && !isSelectedCourseLocked ? (
                    <Button color="warning" size="small" onClick={() => setExclusionUnlockDialogOpen(true)}>
                      Entsperren
                    </Button>
                  ) : null}
                >
                  Das Ausschlussverfahren ist finalisiert und nur lesbar. Änderungen sind erst nach einer Entsperrung mit Begründung möglich.
                </Alert>
              ) : null}

              {exclusionValidationVisible && exclusionValidation.hasErrors ? (
                <Alert severity="warning">{getExclusionValidationMessage(exclusionValidation)}</Alert>
              ) : null}

              {exclusionParticipant ? (
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 700, textDecoration: exclusionParticipant.status === 'withdrawn' ? 'line-through' : 'none' }}>
                      {exclusionParticipant.full_name}
                    </Typography>
                    {exclusionParticipant.status === 'withdrawn' ? (
                      <Chip color="error" label={selectedExclusionRecord?.withdrawnOnDayLabel || (exclusionParticipant.withdrawn_on_day_id ? `Ausgeschieden ab Tag ${exclusionParticipant.withdrawn_on_day_id}` : 'Ausgeschieden')} />
                    ) : null}
                    {selectedExclusionRecord?.status ? <Chip label={selectedExclusionRecord.status === 'finalized' ? 'Finalisiert' : 'Entwurf'} color={selectedExclusionRecord.status === 'finalized' ? 'success' : 'warning'} /> : null}
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
                        <Stack spacing={0.75}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                            Gesamtleistung
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Ø {formatAverage(getParticipantOverallSummary(exclusionParticipant)?.overallAverage)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getParticipantOverallSummary(exclusionParticipant)?.performanceLabel || getSrhtCategoryScaleTone(getParticipantOverallSummary(exclusionParticipant)?.overallAverage).label}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Grid>
                    {(exclusionWeeklySummaries || []).map((summary) => (
                      <Grid item xs={12} md={4} key={`exclusion-week-${summary.weekNumber}`}>
                        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
                          <Stack spacing={0.75}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                              {`Finalisierte Wochenleistung Woche ${summary.weekNumber}`}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Ø {formatAverage(summary.overallAverage)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {summary.performanceLabel || getSrhtCategoryScaleTone(summary.overallAverage).label}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Ausführliche Begründung für den Ausschluss aus dem Lehrgang"
                    value={exclusionForm.detailedReasonText}
                    onChange={(event) => setExclusionForm((currentValue) => ({ ...currentValue, detailedReasonText: event.target.value }))}
                    disabled={isExclusionReadOnly}
                  />

                  <Stack spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Wann und in welchem Umfang wurde die Leitung der Höhenrettung über die defizitäre Leistung des Teilnehmers informiert?
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<PlaylistAddIcon />}
                        onClick={handleAddExclusionReport}
                        disabled={isExclusionReadOnly || exclusionForm.reports.length >= SRHT_EXCLUSION_MAX_REPORTS}
                      >
                        Weiteren Bericht hinzufügen
                      </Button>
                    </Stack>
                    {exclusionForm.reports.map((report, index) => {
                      const reportHasRangeError = exclusionValidationVisible && exclusionValidation.outOfRangeReportIndexes.includes(index);
                      const reportHasIncompleteError = exclusionValidationVisible && exclusionValidation.incompleteReportIndexes.includes(index);

                      return (
                        <Paper key={`exclusion-report-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                          <Stack spacing={1.25}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                              <DateInputField
                                label={`Bericht ${index + 1} - Datum`}
                                value={report.reportDate}
                                onChange={(value) => handleExclusionReportChange(index, { reportDate: value })}
                                disabled={isExclusionReadOnly}
                                error={reportHasRangeError || reportHasIncompleteError}
                                helperText={reportHasRangeError ? 'Datum muss innerhalb des Lehrgangs liegen.' : reportHasIncompleteError ? 'Datum und Freitext sind gemeinsam erforderlich.' : ' '}
                                sx={{ minWidth: { sm: 220 } }}
                              />
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Bericht an die Leitung der Höhenrettung"
                                value={report.reportText}
                                onChange={(event) => handleExclusionReportChange(index, { reportText: event.target.value })}
                                disabled={isExclusionReadOnly}
                                error={reportHasIncompleteError}
                              />
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => handleRemoveExclusionReport(index)}
                                disabled={isExclusionReadOnly || exclusionForm.reports.length === 1}
                              >
                                Entfernen
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>

                  <FormControl fullWidth error={exclusionValidationVisible && exclusionValidation.missingHearingUsers}>
                    <InputLabel id="srht-exclusion-hearings-label">Angehörte Lehrhöhenretter und Leiter</InputLabel>
                    <Select
                      labelId="srht-exclusion-hearings-label"
                      label="Angehörte Lehrhöhenretter und Leiter"
                      multiple
                      value={exclusionForm.hearingUserIds}
                      onChange={(event) => setExclusionForm((currentValue) => ({
                        ...currentValue,
                        hearingUserIds: normalizeMultiSelectValue(event.target.value).map((value) => Number(value)),
                      }))}
                      renderValue={(selectedValues) => selectedValues
                        .map((value) => (referenceOptions.dayInstructorOptions || []).find((option) => Number(option.id) === Number(value))?.fullName || value)
                        .join(', ')}
                      disabled={isExclusionReadOnly}
                    >
                      {(referenceOptions.dayInstructorOptions || []).map((option) => (
                        <MenuItem key={`exclusion-hearing-${option.id}`} value={option.id}>
                          {option.fullName}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {exclusionValidationVisible && exclusionValidation.missingHearingUsers ? 'Bitte mindestens eine angehörte Person auswählen.' : 'Folgende Lehrhöhenretter und Leiter wurden bzgl. der Leistung des Teilnehmers angehört.'}
                    </FormHelperText>
                  </FormControl>

                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(exclusionForm.exclusionConfirmed)}
                        onChange={(event) => setExclusionForm((currentValue) => ({
                          ...currentValue,
                          exclusionConfirmed: event.target.checked,
                        }))}
                        disabled={isExclusionReadOnly}
                      />
                    )}
                    label="Der Ausschluss des Teilnehmers wird sowohl von der Lehrgangsleitung, als auch von der Leitung der Höhenrettung als notwendig erachtet."
                  />

                  <DateInputField
                    fullWidth
                    label="Der Ausschluss aus dem Lehrgang wird dem Teilnehmer am folgenden Datum mitgeteilt"
                    value={exclusionForm.participantNotifiedDate}
                    onChange={(value) => setExclusionForm((currentValue) => ({ ...currentValue, participantNotifiedDate: value }))}
                    disabled={isExclusionReadOnly}
                    error={exclusionValidationVisible && (exclusionValidation.participantNotifiedOutOfRange || exclusionValidation.participantNotifiedBeforeReports || exclusionValidation.missingParticipantNotifiedDate)}
                    helperText={exclusionValidationVisible && exclusionValidation.participantNotifiedOutOfRange
                      ? 'Datum muss innerhalb des Lehrgangs liegen.'
                      : exclusionValidationVisible && exclusionValidation.participantNotifiedBeforeReports
                        ? 'Die Leitung der Höhenrettung muss vor der Mitteilung an den Teilnehmer informiert worden sein.'
                        : exclusionValidationVisible && exclusionValidation.missingParticipantNotifiedDate
                          ? 'Bitte ein Mitteilungsdatum angeben.'
                          : ' '}
                  />
                </Stack>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseExclusionDialog}>Schließen</Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportExclusionPdf}
              disabled={busy || !selectedExclusionRecord}
            >
              PDF herunterladen
            </Button>
            <Button variant="contained" onClick={handleSaveExclusion} disabled={busy || isExclusionReadOnly}>
              Speichern
            </Button>
            <Button variant="contained" color="error" onClick={handleFinalizeExclusion} disabled={busy || isExclusionReadOnly}>
              Teilnehmer ausschließen
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={exclusionUnlockDialogOpen} onClose={() => setExclusionUnlockDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Ausschlussverfahren entsperren</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography>
                Bitte begründen Sie, warum das finalisierte Ausschlussverfahren erneut bearbeitet werden muss.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Begründung zum Entsperren"
                value={exclusionUnlockReason}
                onChange={(event) => setExclusionUnlockReason(event.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExclusionUnlockDialogOpen(false)}>Abbrechen</Button>
            <Button variant="contained" color="warning" onClick={handleUnlockExclusion} disabled={busy || !String(exclusionUnlockReason || '').trim()}>
              Entsperren
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={closeCourseDialogOpen} onClose={() => setCloseCourseDialogOpen(false)}>
          <DialogTitle>Lehrgang abschließen</DialogTitle>
          <DialogContent>
            <Typography>
              {selectedCourse ? `Soll der Lehrgang „${selectedCourse.label}“ wirklich abgeschlossen werden? Anschließend sind Änderungen erst nach einer Wiederöffnung mit Begründung möglich.` : ''}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseCourseDialogOpen(false)}>Abbrechen</Button>
            <Button variant="contained" color="success" onClick={handleCloseCourse} disabled={busy}>
              Abschließen
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={unlockCourseDialogOpen} onClose={() => setUnlockCourseDialogOpen(false)}>
          <DialogTitle>Lehrgang wieder öffnen</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1, minWidth: { xs: 'auto', sm: 420 } }}>
              <Typography>
                {selectedCourse ? `Bitte begründen Sie, warum der abgeschlossene Lehrgang „${selectedCourse.label}“ erneut bearbeitet werden muss.` : ''}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Begründung"
                value={courseUnlockReason}
                onChange={(event) => setCourseUnlockReason(event.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnlockCourseDialogOpen(false)}>Abbrechen</Button>
            <Button variant="contained" color="warning" onClick={handleUnlockCourse} disabled={busy || !courseUnlockReason.trim()}>
              Wieder öffnen
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteCourseDialogOpen} onClose={() => setDeleteCourseDialogOpen(false)}>
          <DialogTitle>Lehrgang löschen</DialogTitle>
          <DialogContent>
            <Typography>
              {selectedCourse ? `Soll der Lehrgang „${selectedCourse.label}“ wirklich gelöscht werden? Dieser Vorgang kann nicht rückgängig gemacht werden.` : ''}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteCourseDialogOpen(false)}>Abbrechen</Button>
            <Button variant="contained" color="error" onClick={handleDeleteCourse} disabled={busy || !canDeleteSelectedCourse}>
              Löschen
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isTrainingDayDialogOpen} onClose={() => setTrainingDayDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Lehrgangstage auswählen</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Zeitraum: {formatDate(createForm.startDate)} bis {formatDate(createForm.endDate)}
              </Typography>
              {isTrainingDayRangeReady ? (
                <Grid container spacing={2}>
                  {trainingDayCalendarMonths.map((month) => (
                    <Grid item xs={12} md={trainingDayCalendarMonths.length > 1 ? 6 : 12} key={month.monthKey}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                            {month.label}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                              gap: 0.75,
                            }}
                          >
                            {SRHT_COURSE_WEEKDAY_LABELS.map((weekdayLabel) => (
                              <Typography
                                key={weekdayLabel}
                                variant="caption"
                                sx={{ fontWeight: 700, textAlign: 'center', color: 'text.secondary' }}
                              >
                                {weekdayLabel}
                              </Typography>
                            ))}
                            {month.weeks.flat().map((day, index) => (
                              day ? (
                                <Box
                                  key={day.isoDate}
                                  component="button"
                                  type="button"
                                  onClick={() => handleToggleTrainingDay(day.isoDate)}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: day.isSelected ? 'primary.main' : 'divider',
                                    backgroundColor: day.isSelected ? 'primary.main' : 'background.paper',
                                    color: day.isSelected ? 'primary.contrastText' : 'text.primary',
                                    borderRadius: 1.5,
                                    minHeight: 42,
                                    font: 'inherit',
                                    cursor: 'pointer',
                                    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                      backgroundColor: day.isSelected ? 'primary.dark' : 'action.hover',
                                    },
                                  }}
                                >
                                  {day.dayOfMonth}
                                </Box>
                              ) : (
                                <Box key={`${month.monthKey}-empty-${index}`} sx={{ minHeight: 42 }} />
                              )
                            ))}
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">Bitte zuerst einen gültigen Zeitraum festlegen.</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setCreateForm((currentValue) => ({
                ...currentValue,
                trainingDays: [],
              }))}
            >
              Auswahl leeren
            </Button>
            <Button onClick={() => setTrainingDayDialogOpen(false)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
}

export default LehrgangSrhtPage;